<template id="app">
  <SplashLoading
    v-if="loading.active"
    ref="splashLoading"
    @animationEnd="loading.active = false"
  />

  <AppLayout v-else class="app-default-layout">
    <template #content>
      <RouteView />
    </template>

    <template #footer>
      <div ref="appFooter" class="flex flex-col">
        <transition name="slide-fade" appear>
          <CompanyRatingRate
            v-if="rateLastOrder"
            class="rate-last-order"
            @close="rateLastOrder = undefined"
            @rated="rateLastOrder = undefined"
            v-bind="rateLastOrder"
            shadow
            showCloseButton
          />
        </transition>

        <transition name="slide-up" appear>
          <BottomBar v-if="showBottomBar" />
        </transition>
      </div>
    </template>
  </AppLayout>

  <AppSidebar
    v-if="user && mountUserSidebar"
    @closed="mountUserSidebar = false"
    ref="userSidebar"
  >
    <UserSidebar />
  </AppSidebar>

  <AppModal
    v-for="dialog in dialogs"
    :ref="`dialog-${dialog.id}`"
    :key="`dialog-${dialog.id}`"
    v-bind="dialog"
    :goBackOnClosed="false"
    no-padding
    class="app-dialog"
  >
    <AppLayout>
      <AppLayoutHeader v-if="dialog.title" class="p-6 mb--6">
        <h2 v-html="dialog.title" class="m-0"></h2>
      </AppLayoutHeader>

      <AppLayoutContent v-if="dialog.content" class="p-6">
        <div v-html="dialog.content"></div>
      </AppLayoutContent>

      <AppLayoutFooter
        v-if="dialog.buttons.length > 0"
        :fixed="false"
        class="p-6 mt--6"
      >
        <AppButtons :column="true">
          <AppButton
            v-for="(button, index) in dialog.buttons"
            :key="index"
            v-bind="button"
            v-html="button.content ?? button.label ?? button.text"
            fullWidth
          />
        </AppButtons>
      </AppLayoutFooter>
    </AppLayout>
  </AppModal>

  <AppSnackbars
    v-if="snackbars.length > 0"
    ref="snackbars"
    :snackbars="snackbars"
    @closed="removeSnackbar"
  />

  <AppDrawer
    v-for="(drawer, drawerIndex) in drawers"
    :key="`drawer-${drawerIndex}`"
    :ref="`drawer-${drawerIndex}`"
    v-bind="drawer"
  >
    <component
      :is="drawer.component"
      v-bind="drawer.props"
      :ref="`drawer-${drawerIndex}-component`"
    />
  </AppDrawer>
</template>

<script>
import { markRaw, createApp } from 'vue'
import { app as $app } from '@/main'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import isUaWebview from 'is-ua-webview'
import is_numeric from 'locutus/php/var/is_numeric'
import {
  default as CardValidator,
  cardholderName,
  expirationDate,
} from 'card-validator'
import crypto from 'crypto'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import StringMask from 'string-mask'
import localForage from 'localforage'
import pagarme from 'pagarme'
import { createGtm } from '@gtm-support/vue-gtm'

import { version as appVersion } from '@/../package.json'
import {
  THE_TRUE_ARRAY,
  DIALOG_TYPE_CONFIRM,
  DIALOG_TYPE_ALERT,
  DIALOG_TYPE_LOADING,
  DELIVERY_TIME_RULE_TYPE_OPERATIONAL,
  DELIVERY_TIME_RULE_TYPE_SECTION,
  DELIVERY_TIME_RULE_TYPE_CATEGORY,
  DELIVERY_TIME_RULE_TYPE_PRODUCT,
  DELIVERY_TIME_RULE_TYPE_GENERAL,
  DELIVERY_TIME_MODE_LONGEST_TIME,
  DELIVERY_TIME_MODE_SHORTEST_TIME,
  DELIVERY_TIME_MODE_SUM_TIMES,
  DELIVERY_TIME_MODE_AVERAGE_TIMES,
  PRICE_TYPE_AVERAGE,
  PRICE_TYPE_MORE_EXPENSIVE,
  PRICE_TYPE_CHEAPER,
  ROUND_PRICE_UP,
  ROUND_PRICE_DOWN,
  SCHEDULING_MODE_FORCED,
  SCHEDULING_MODE_DISABLED,
  SCHEDULING_MODE_OPTIONAL,
  EXTRA_TYPE_NUMBER,
  SECTION_TYPE_PIZZA,
  ORDER_ORIGIN_COMPANY,
} from '@/constants'
import Configs from '@/configs'
import pagarmeConfigs from '@/configs/Pagar.me.js'

import {
  getSettingValue,
  importScript,
  equal,
  uuid,
  calculate,
  slug,
  clone,
  defaultFilterFunction,
  getOnlyNumbers,
  defaultSortFunction,
  updateObject,
  getPaymentCardExpiry,
  detectOperatingSystem,
  sanitizePaymentCardHolderName,
  validatePaymentCardNumber,
  clientSupportsAVIF,
} from '@/helpers'

import UserChangePhoneNumber from '@/components/User/ChangePhoneNumber.vue'
import RouteView from '@/components/RouteView.vue'
import SplashLoading from '@/pages/SplashLoading.vue'
import UserSidebar from '@/components/User/Sidebar.vue'
import BottomBar from '@/components/BottomBar.vue'
import CompanyRatingRate from '@/components/Company/Rating/Rate.vue'

import User from '@/components/User.vue'

import AppDrawer from '@/components/App/Drawer.vue'
import AppWrapper from '@/components/App/Wrapper.vue'
import AppLayout from '@/components/App/Layout.vue'
import AppLayoutHeader from '@/components/App/Layout/Header.vue'
import AppLayoutContent from '@/components/App/Layout/Content.vue'
import AppLayoutFooter from '@/components/App/Layout/Footer.vue'
import AppButtons from '@/components/App/Buttons.vue'
import AppButton from '@/components/App/Buttons/Button.vue'
import AppModal from '@/components/App/Modal.vue'
import AppSnackbars from '@/components/App/Snackbars.vue'
import AppCard from '@/components/App/Card.vue'
import AppSidebar from '@/components/App/Sidebar.vue'
import { normalizeOrder } from '@/utils/Company/Order'

export default {
  name: 'App',

  components: {
    CompanyRatingRate,
    SplashLoading,
    RouteView,
    BottomBar,
    UserSidebar,
    AppModal,
    AppDrawer,
    AppLayout,
    AppLayoutHeader,
    AppLayoutContent,
    AppLayoutFooter,
    AppCard,
    AppSidebar,
    AppButtons,
    AppButton,
    AppWrapper,
    AppSnackbars,
  },

  data() {
    return {
      DIALOG_TYPE_ALERT,
      DIALOG_TYPE_CONFIRM,
      DIALOG_TYPE_LOADING,

      clientSupportsAVIF: undefined,
      user: undefined,
      hasLoadedUser: false,
      mountUserSidebar: false,

      rateLastOrder: undefined,
      pointPrice: {
        price: undefined,
        timestamp: undefined,
      },
      drawers: [],
      activeWrappers: [],
      platform: 'Web App',
      os: detectOperatingSystem(),
      productName: Configs.name,
      version: Configs.version,
      configs: Configs,
      loading: {
        active: true,
        text: null,
      },
      snackbars: [],
      dialogs: [],
      isGoToScreen: false,
      online: true,
      api: {
        defaultVersion: 0,
        sessionToken: undefined,
      },

      lastVisitedCityId: undefined,
    }
  },

  computed: {
    showRetrospective() {
      return new Date().getMonth() === 11 && app.user
    },

    isWebView() {
      return isUaWebview(navigator.userAgent)
    },

    environment() {
      return import.meta.env.NODE_ENV
    },

    defaultDomain() {
      return this.environment === 'development'
        ? 'localhost'
        : 'deliveryinbox.com.br'
    },

    support: {
      cache: false,

      get() {
        return this.$refs.support || {}
      },
    },

    viewportHeight: {
      get() {
        return window.innerHeight
      },

      set(value) {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${value}px`,
        )
      },
    },

    showBottomBar() {
      return ['selectCompany', 'search'].includes(this.$route.name)
    },
  },

  watch: {
    '$route.params.cityId': {
      immediate: true,
      handler(cityId) {
        if (cityId && this.lastVisitedCityId !== cityId) {
          this.lastVisitedCityId = cityId
          this.setLastVisitedCityId(cityId)
        }
      },
    },

    user: {
      handler(user, oldUser) {
        if (equal(user, oldUser) === true) return
        if (!user && oldUser) {
          app.$emit('logout', oldUser)
        } else if (user?.id !== oldUser?.id) {
          app.$emit('login', user, oldUser)
        } else if (equal(user, oldUser) === false) {
          app.$emit('userUpdated', user, oldUser)
        }
      },
    },
  },

  async beforeCreate() {
    window.app = this

    window.addEventListener(
      'offline',
      event => {
        app.online = false
        app.$emit('offline', event)
      },
      false,
    )
    window.addEventListener(
      'online',
      event => {
        app.online = true
        app.$emit('online', event)
      },
      false,
    )
  },

  async created() {
    // this.loadDatadog().catch(console.error)

    this.refreshViewportHeight().registerListeners()
    this.loadClientSupportsAVIF()

    window.addEventListener('resize', this.refreshViewportHeight)
  },

  async mounted() {
    await this.init()

    const { appFooter } = this.$refs
    const resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        '--app-footer-height',
        `${appFooter.offsetHeight}px`,
      )
    })

    resizeObserver.observe(appFooter)
  },

  methods: {
    getUserTotalOrders: User.methods.getTotalOrders,
    getUserEstablishmentTotalOrders: User.methods.getEstablishmentTotalOrders,

    async loadClientSupportsAVIF() {
      return (this.clientSupportsAVIF = await clientSupportsAVIF())
    },

    async makeRDStationConversion(payload, { apiKey }) {
      const body = {
        event_type: 'CONVERSION',
        event_family: 'CDP',
        payload,
      }
      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }

      return fetch(
        `https://api.rd.services/platform/conversions?api_key=${apiKey}`,
        options,
      ).then(response => response.json())
    },

    async showUserChangePhoneNumber() {
      return new Promise(async (resolve, reject) => {
        const { user } = this
        const {
          instances: { drawer, component },
        } = await app.openComponentInDrawer(UserChangePhoneNumber, {
          props: {
            user,
          },
        })

        component
          .$on('changed', phoneNumber => {
            // updateObject(user, updatedUser)
            // app.user = updatedUser
            user.phoneNumber = phoneNumber

            drawer.close()
            resolve(phoneNumber)
          })
          .$on('close', drawer.close)

        drawer.$on('closed', () => {
          component.$destroy()
          drawer.$destroy()
          reject()
        })

        drawer.open()
      })
    },

    async init() {
      const { splashLoading } = this.$refs

      await splashLoading?.startAnimation?.()

      this.hasLoadedUser = false

      // await this.checkOldSubdomain()
      await this.loadCordova()

      this.enablePrintScreen(true)
      navigator?.splashscreen?.hide?.()

      await this.checkParentStorage()
      await this.loadSessionToken()
      await this.loadOneSignal()

      await Promise.all([
        this.checkUTM(),
        this.enableDeviceReadyFallback(),
        this.loadGoogleTagManager(),
        //this.loadSmartlook(),
        // this.connectSocket(),
        this.enableIntentEvent(),
        this.checkAffiliateCode(),
        this.loadUser().finally(() => (this.hasLoadedUser = true)),
        this.loadLastVisitedCityId(),
      ])

      this.checkFirstExecution()

      await this.goToFirstRoute()
      // this.checkGeolocation()

      splashLoading?.endAnimation?.()
    },

    async enablePrintScreen(value) {
      if (app.platform !== 'App') return

      return new Promise((resolve, reject) => {
        window.plugins?.preventscreenshot?.[value ? 'enable' : 'disable']?.(
          resolve,
          reject,
        )
      })
    },

    async checkFirstExecution() {
      const firstExecutionKey = 'firstExecution'
      const firstExecution = await this.getItem(firstExecutionKey)

      if (!firstExecution) {
        const nowISO = new Date().toISOString()

        await this.setItem(firstExecutionKey, nowISO)
        await this.trackEvent('firstExecution', {
          date: nowISO,
        })
      }
    },

    openInExternalBrowser(url) {
      const openMethod = window.cordova?.InAppBrowser?.open ?? window.open

      return openMethod(url, '_system')
    },

    async getUser() {
      return new Promise(resolve => {
        const { user, hasLoadedUser } = this

        if (hasLoadedUser === false) {
          let unwatch

          unwatch = this.$watch(
            'hasLoadedUser',
            value => {
              if (value) {
                unwatch()
                resolve(this.user)
              }
            },
            {
              immediate: true,
            },
          )
        } else resolve(user)
      })
    },

    async checkOldSubdomain() {
      // if (location.hostname !== 'localhost') return this.updateStorageFromUrl()
      if (location.hostname !== 'mobile.deliveryinbox.com.br')
        return this.updateStorageFromUrl()

      return this.redirectToNewSubdomain()
    },

    async redirectToNewSubdomain() {
      const updatedSubdomainStorageKey = 'updatedSubdomain'
      const updatedSubdomain = await app.getItem(updatedSubdomainStorageKey)

      let storageData = {}

      if (!updatedSubdomain) {
        const { user } = app
        const [gaId, lastCityId, sessionToken, paymentCardsData] =
          await Promise.all([
            this.getItem('gaId'),
            this.getItem('lastCityId'),
            this.getItem('sessionToken'),
            this.getPaymentCardsData(user),
          ])

        storageData = {
          gaId,
          lastCityId,
          sessionToken,
        }

        if (paymentCardsData) {
          const paymentCardsStorageKey = this.getPaymentCardsStorageKey(user)

          storageData[paymentCardsStorageKey] = paymentCardsData
        }

        Object.keys(storageData).forEach(
          key =>
            [undefined, null].includes(storageData[key]) &&
            delete storageData[key],
        )
      }

      let newUrl = `https://app.deliveryinbox.com.br/#${this.$route.path}`
      // let newUrl = `http://127.0.0.1:8080/#${this.$route.path}`

      if (Object.keys(storageData).length > 0) {
        storageData = JSON.stringify(storageData)
        storageData = btoa(storageData)

        newUrl = `${newUrl}?sd=${storageData}`
      }

      if (!updatedSubdomain) {
        const currentISODate = new Date().toISOString()

        await app.setItem(updatedSubdomainStorageKey, currentISODate)
      }

      location.href = newUrl
    },

    async updateStorageFromUrl() {
      return new Promise(async (resolve, reject) => {
        try {
          let { sd: storageData } = this.$route.query

          if (!storageData) return resolve()

          storageData = atob(storageData)
          storageData = JSON.parse(storageData)

          await this.setItems(storageData)

          await this.$router.replace({
            ...this.$route,
            query: {
              ...this.$route.query,
              sd: undefined,
            },
          })

          return resolve()
        } catch (error) {
          reject(error)
        }
      })
    },

    async loadLastVisitedCityId() {
      return (this.lastVisitedCityId = await this.getLastVisitedCityId())
    },

    goToRetrospective(year = new Date().getFullYear()) {
      return app.goToScreen({
        name: 'userRetrospective',
        params: { year },
      })
    },

    async getLastVisitedCityId() {
      return this.lastVisitedCityId ?? (await this.getItem('lastCityId'))
    },

    async setLastVisitedCityId(cityId) {
      return this.setItem('lastCityId', cityId)
    },

    async loadDatadog() {
      const applicationId =
        import.meta.env.VITE_DATADOG_APPLICATION_ID ??
        import.meta.env.DATADOG_APPLICATION_ID

      if (!applicationId) throw new Error('Datadog application id not set!')

      const clientToken =
        import.meta.env.VITE_DATADOG_CLIENT_TOKEN ??
        import.meta.env.DATADOG_CLIENT_TOKEN

      if (!clientToken) throw new Error('Datadog client token not set!')

      const { datadogRum } = await import('@datadog/browser-rum')

      datadogRum.init({
        applicationId,
        clientToken,
        site:
          import.meta.env.VITE_DATADOG_SITE ??
          import.meta.env.DATADOG_SITE ??
          'datadoghq.com',
        env: import.meta.env.NODE_ENV ?? import.meta.env.ENVIRONMENT,
        version: appVersion,
        trackInteractions: true,
      })

      return datadogRum.startSessionReplayRecording()
    },

    alertVerifiedPrice() {
      return app.alert({
        title: 'Preço verificado...',
        content: `
          Empresas com este selo são aquelas que já tiveram seu cardápio revisado pelo nosso time e possuem o mesmo preço tanto no Delivery In Box quanto em outros canais de venda.<br><br>
          Fique ligado! Opte sempre pelas empresas verificadas e <b>pague menos na sua compra</b>. 😉
        `,
      })
    },

    async editUser(data = {}) {
      const updatedUser = await app.callApi({
        method: 'PATCH',
        version: 1,
        action: 'me',
        data,
      })

      app.user = updatedUser

      return updatedUser
    },

    async saveLogin({ sessionToken, user }) {
      if (!sessionToken)
        throw new Error('[app.saveLogin] sessionToken not defined!')

      await app.setSessionToken(sessionToken)

      if (user) app.user = user
      else await this.loadUser(sessionToken)
    },

    async getUserFromSessionToken(sessionToken) {
      if (!sessionToken) return

      try {
        return await app.callApi({
          sessionToken,
          method: 'GET',
          version: 1,
          action: 'me',
        })
      } catch (e) {
        const code = e?.code ?? e?.response?.data?.code ?? e?.response?.code

        console.error(e)

        switch (code) {
          case 'SESSION_TOKEN_NOT_FOUND':
            app.removeSessionToken()
        }
      }
    },

    async loadSessionToken() {
      const sessionToken =
        this.$route.query.sessionToken ??
        (await this.getItem('sessionToken')) ??
        (await this.getItem('authToken')) ??
        (await this.getItem('apiToken'))

      this.api.sessionToken = sessionToken

      return sessionToken
    },

    getSessionToken() {
      return this.api.sessionToken
    },

    async setSessionToken(sessionToken) {
      if (!sessionToken) return this.removeSessionToken()

      await this.setItem('sessionToken', sessionToken)

      this.api.sessionToken = sessionToken

      return this
    },

    async removeSessionToken() {
      app.api.sessionToken = undefined

      await Promise.all([
        app.removeItem('sessionToken'),
        app.removeItem('authToken'),
        app.removeItem('apiToken'),
      ])
    },

    async loadUserFromSessionToken(sessionToken) {
      try {
        return (app.user = await app.getUserFromSessionToken(sessionToken))
      } catch (e) {
        switch (e?.code) {
          case 'SESSION_DISCONNECTED':
          case 'AUTH_TOKEN_NOT_FOUND':
          case 'AUTH_TOKEN_NOT_DEFINED':
          case 'SESSION_TOKEN_NOT_DEFINED':
          case 'LOGGED_OUT':
          case 'USER_DISABLED':
            await this.logout(sessionToken)
            break

          default:
            console.error(e)
            break
        }
      }
    },

    registerListeners() {
      this.$on('newIntent', this.onNewIntent)

      this.$on('userUpdated', user =>
        Promise.all([
          this.trackEvent('userUpdated', user),
          this.updateSupportUser(user),
          //this.updateSmartlookUser(user),
        ]),
      )

      this.$on('logout', user => {
        Promise.all([
          this.trackEvent('logout', user),
          this.updateOneSignalUser(),
        ])
      })

      this.$on('login', async user =>
        Promise.all([
          this.trackEvent('login', user),
          //this.updateSmartlookUser(user),
          this.updateSupportUser(user),
          this.updateGoogleAnalyticsUser(user),
          this.updateUserOnesignalDeviceId(),
          this.updateUserOneSignalTags(user),
          this.updateOneSignalUser(user),
          this.checkLastOrderRating(user),
        ]),
      )

      return this
    },

    async checkLastOrderRating(user) {
      let { origin } = this.$route.query

      origin = Number.parseInt(origin)

      if (origin === ORDER_ORIGIN_COMPANY) return

      const lastOrder = (
        await app.callApi({
          method: 'GET',
          version: 1,
          action: 'orders',
          query: {
            fields: ['companyId'],
            userId: user.id,
            isValid: true,
            sort: '-createdAt',
            limit: 1,
          },
        })
      )?.data?.[0]

      if (!lastOrder) return

      const companyId = lastOrder?.company?.id ?? lastOrder.companyId
      const rating = (
        await app.callApi({
          method: 'GET',
          version: 1,
          action: 'companies/ratings',
          query: {
            fields: ['createdAt'],
            userId: user.id,
            companyId,
            sort: '-createdAt',
          },
        })
      )?.data?.[0]

      if (rating?.createdAt >= lastOrder.createdAt) return

      const company = await app.callApi({
        method: 'GET',
        version: 1,
        action: `companies/${companyId}`,
      })

      this.rateLastOrder = {
        company,
        dontLoadUserRating: true,
      }
    },

    async updateOneSignalUser({ id, email, phoneNumber } = {}) {
      const oneSignal = await this.getOneSignal()
      const promises = []

      if (id) {
        promises.push(
          (oneSignal?.login ?? oneSignal?.setExternalUserId)?.(id),
          oneSignal?.setEmail?.(email),
        )

        if (phoneNumber) promises.push(oneSignal?.setSMSNumber?.(phoneNumber))
      } else {
        promises.push(
          (oneSignal?.logoutEmail ?? oneSignal?.removeExternalUserId)?.(),
        )
      }

      return Promise.all(promises)
    },

    refreshViewportHeight() {
      app.viewportHeight = window.innerHeight

      return this
    },

    goToFeedback() {
      return app.goToScreen({
        name: 'feedback',
      })
    },

    canResolveRoute(route) {
      const result = this.$router.resolve(route)

      return result?.resolved?.matched?.length > 0
    },

    async getSection({ sectionId }) {
      return app.callApi({
        method: 'GET',
        version: 1,
        action: `sections/${sectionId}`,
      })
    },

    async getSectionCategory(categoryId) {
      return app.callApi({
        method: 'GET',
        version: 1,
        action: `sections/categories/${categoryId}`,
      })
    },

    async getSectionCategories(query = {}) {
      return app
        .callApi({
          method: 'GET',
          version: 1,
          action: 'sections/categories',
          query,
        })
        .then(response => response?.data ?? [])
    },

    isValidEmail(email) {
      return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        email,
      )
    },

    getCurrentFullWrapper() {
      const activeWrappers = this.activeWrappers

      return activeWrappers[activeWrappers.length - 1]
    },

    async getUserAdresses() {
      return (
        (await app.callApi({
          method: 'GET',
          version: 1,
          action: 'me/adresses',
        })) ?? []
      )
    },

    async horizontalScrollElement({ element, behavior = 'smooth' }) {
      const smoothscroll = await import('smoothscroll-polyfill')
      const viewport = element.parentNode
      const viewportWidth = parseFloat(getComputedStyle(viewport).width)
      const elementWidth = parseFloat(getComputedStyle(element).width)
      const left = element.offsetLeft - viewportWidth / 2 + elementWidth / 2

      smoothscroll.polyfill()

      return viewport.scrollTo({
        left,
        behavior,
      })
    },

    async scrollToElement({
      element,
      behavior = 'auto',
      block = 'start',
      inline = 'nearest',
    }) {
      const smoothscroll = await import('smoothscroll-polyfill')

      smoothscroll.polyfill()

      return element.scrollIntoView({
        behavior,
        block,
        inline,
      })
    },

    observeElementVisibility({ element, handler }) {
      const observer = new IntersectionObserver(
        entries => {
          const entry = entries[0]
          const isIntersecting = entry.isIntersecting

          if (isIntersecting === true) handler()
        },
        { threshold: 0 },
      )

      observer.observe(element)

      return observer
    },

    goToContact() {
      return app.goToScreen({
        name: 'contact',
      })
    },

    async getNeighborhoods({ cityId }) {
      const { data: neighborhoods = [] } = await app.callApi({
        method: 'GET',
        version: 1,
        action: 'cities/neighborhoods',
        query: {
          cityId,
          sort: 'name',
        },
      })

      return neighborhoods
    },

    async getCities({ stateId }) {
      return app
        .callApi({
          method: 'GET',
          version: 1,
          action: 'cities',
          query: {
            stateId,
            sort: 'name',
          },
        })
        .then(response => response?.data ?? [])
    },

    async getStates({ countryId }) {
      return app
        .callApi({
          method: 'GET',
          version: 1,
          action: 'states',
          query: {
            countryId,
            sort: 'name',
          },
        })
        .then(response => response?.data ?? [])
    },

    async getCountries() {
      return app
        .callApi({
          method: 'GET',
          version: 1,
          action: 'countries',
          query: {
            sort: 'name',
          },
        })
        .then(response => response?.data ?? [])
    },

    processList({ list = [], filterCallback, sortCallback } = {}) {
      if (Array.isArray(list) === false) list = []

      return list
        .filter(item => {
          if (
            typeof filterCallback === 'function' &&
            filterCallback(item) === false
          )
            return false

          return defaultFilterFunction(item)
        })
        .sort((i1, i2) => {
          if (typeof sortCallback === 'function') return sortCallback(i1, i2)

          return defaultSortFunction(i1, i2)
        })
    },

    async showUserSidebar() {
      if (!app.user) return app.goToLogin()

      this.mountUserSidebar = true

      await this.$nextTick()

      return this.$refs.userSidebar.open()
    },

    async closeUserSidebar() {
      return this.$refs.userSidebar.close()
    },

    getFirstWord(string) {
      return string.split(' ', 1)[0]
    },

    async updateUserOneSignalTags(user) {
      if (!user) return

      const oneSignal = await this.getOneSignal()

      if (!oneSignal) return

      const cityId = user?.location?.city?.id
      const registeredIn = user?.registeredIn
      const tags = {}

      if (cityId) tags.cityId = cityId

      if (registeredIn)
        tags.registeredInTimestamp = new Date(registeredIn).getTime()

      const userSettings = user?.settings ?? []
      const funNotificationsEnabled =
        getSettingValue(userSettings, 'notifications.fun.enabled') ?? true
      const promotionsNotificationsEnabled =
        getSettingValue(userSettings, 'notifications.promotions.enabled') ??
        true
      const ordersNotificationsEnabled =
        getSettingValue(userSettings, 'notifications.orders.enabled') ?? true

      tags.funNotificationsEnabled = funNotificationsEnabled
      tags.promotionsNotificationsEnabled = promotionsNotificationsEnabled
      tags.ordersNotificationsEnabled = ordersNotificationsEnabled

      return oneSignal.sendTags(tags)
    },

    async getOneSignal() {
      return window.plugins?.OneSignal ?? window.OneSignal
    },

    setBackgroundMode({
      enable,
      notificationTitle = app.productName,
      notificationText,
      notificationIcon,
      notificationColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--primary-color')
        .trim(),
    }) {
      const backgroundMode = window.cordova?.plugins?.backgroundMode

      if (!backgroundMode) {
        console.info('Background mode plugin not found!')
        return
      }

      backgroundMode.setDefaults({
        silent: true,
      })

      const disableWebViewOptimizations = () => {
        try {
          console.info('Disabling WebView optimizations...')
          backgroundMode.disableWebViewOptimizations()
          console.log(
            'WebView optimizations have been deactivated successfully!',
          )
        } catch (e) {
          console.error(
            'There was an error turning off WebView optimizations!',
            e,
          )
        }
      }
      const disableBatteryOptimizations = () => {
        try {
          console.info('Disabling battery optimizations...')
          backgroundMode.disableWebViewOptimizations()
          console.log(
            'Battery optimizations have been deactivated successfully!',
          )
        } catch (e) {
          console.error(
            'There was an error turning off battery optimizations!',
            e,
          )
        }
      }
      if (enable === true) {
        backgroundMode.on('activate', disableWebViewOptimizations)
        backgroundMode.on('enable', disableBatteryOptimizations)
      } else {
        backgroundMode.un('activate', disableWebViewOptimizations)
      }
      try {
        console.info(`Turning ${enable ? 'on' : 'off'} background mode...`)
        backgroundMode.setEnabled(enable)
        console.log(`Background mode ${enable ? 'enabled' : 'disabled'}!`)
      } catch (e) {
        const msg = enable
          ? `An error occurred when activating background mode!`
          : `There was an error turning off background mode!`
        console.error(msg, e)
      }
    },

    async getOrderFromId(orderId) {
      return app
        .callApi({
          method: 'GET',
          action: `orders/${orderId}`,
        })
        .then(response => response?.data)
    },

    async getParentStorageData({ timeout = 10 * 1000 } = {}) {
      return new Promise((resolve, reject) => {
        let timeoutTimer

        try {
          const iframe = document.createElement('iframe')

          timeoutTimer = setTimeout(() => {
            iframe.remove?.()

            reject(new Error('Timeout on loading top storage!'))
          }, timeout)

          window.addEventListener('message', event => {
            // if (allowedOrigins.includes(event.origin) === false) return

            const message = event?.data

            if (message?.type === 'storageData') {
              const storageData = message?.data ?? {}

              resolve(storageData)
            }

            iframe.remove?.()
            clearTimeout(timeoutTimer)
          })

          const sendStorageHref = this.$router.resolve({
            name: 'sendStorageData',
          })?.href
          const iframeSrc =
            app.os === 'iOS'
              ? `ionic://localhost/${sendStorageHref}`
              : `http://localhost/index.html${sendStorageHref}`

          iframe.style.display = 'none'
          iframe.src = iframeSrc

          document.body.appendChild(iframe)
        } catch (e) {
          reject(e)
        }
      })
    },

    async checkParentStorage() {
      try {
        if (app.platform !== 'App' || app.os === 'iOS') return

        if (this.$route.name === 'sendStorageData') {
          const data = await this.getItems()

          window.parent.postMessage({ type: 'storageData', data }, '*')
        } else {
          const checkKey = 'loadedParentStorageAt'
          const loadedParentStorageAt = await this.getItem(checkKey)

          if (loadedParentStorageAt) return

          const parentStorageData = await this.getParentStorageData()

          await this.setItems({
            ...parentStorageData,
            [checkKey]: new Date().toISOString(),
          })

          return window.location.reload()
        }
      } catch (e) {
        console.error(e)
      }
    },

    async loadUser(sessionToken) {
      if (!sessionToken) sessionToken = await app.getSessionToken()

      return app.loadUserFromSessionToken(sessionToken)
    },

    async checkAffiliateCode() {
      const affiliateCode = this.$route.query.affiliateCode

      if (affiliateCode) await app.setItem('affiliateCode', affiliateCode)
    },

    isSelectableExtraItem(extraItem) {
      return extraItem.isVisible !== false && extraItem.active !== false
    },

    getSelectableExtraItems({ items = [] }) {
      return items.filter(app.isSelectableExtraItem)
    },

    getExtraQuantity(extra) {
      return extra.items.reduce(
        (acc, item) => acc + app.getExtraItemQuantity(item),
        0,
      )
    },

    getMinExtraQuantity(extra) {
      let minQuantity = extra?.minTotalQuantity ?? 0

      if (!minQuantity && (extra.isRequired ?? extra.required) === true)
        minQuantity = 1

      if (extra.type !== EXTRA_TYPE_NUMBER) {
        const totalSelectableItems = app.getSelectableExtraItems(extra).length

        if (minQuantity > totalSelectableItems)
          minQuantity = totalSelectableItems
      }

      return minQuantity
    },

    getMaxExtraQuantity(extra) {
      return extra?.maxTotalQuantity ?? extra?.maxValue
    },

    getExtraItemQuantity(extraItem) {
      return (
        extraItem.quantity ??
        extraItem.value ??
        extraItem.defaultQuantity ??
        extraItem.defaultValue ??
        0
      )
    },

    getExtraItemPrice({ extraItem, item, fieldName = 'price' }) {
      if (extraItem.unitPrice !== undefined)
        return extraItem.unitPrice * (extraItem.quantity ?? 1)

      const extraItemOperation = extraItem[fieldName]

      if (extraItemOperation === null) return 0

      let value = 0
      let operation = extraItemOperation
      let extraItemOperator = extraItemOperation?.toString?.()?.charAt?.(0)

      if (extraItemOperator === '=') {
        value = app.getItemProductsPrice({ item, fieldName })
      } else {
        operation = app.getPrice({
          price: extraItemOperation,
          portion: item.portion,
          size: item.size,
          numberProducts: item.products.length,
        })
      }

      if (operation === undefined) return undefined

      return calculate(value, operation) * app.getExtraItemQuantity(extraItem)
    },

    getExtraTotalPrice({ extra, item, fieldName = 'price' }) {
      return extra.items
        .filter(extraItem => app.getExtraItemQuantity(extraItem) !== 0)
        .reduce(
          (acc, extraItem) =>
            (acc += app.getExtraItemPrice({ extraItem, item, fieldName })),
          0,
        )
    },

    getItemSchedulingMode(item) {
      const products = item?.products ?? []
      const productWithDifferentSchedulingMode = products.find(product => {
        const productSchedulingMode = product?.scheduling?.mode

        return [SCHEDULING_MODE_FORCED, SCHEDULING_MODE_DISABLED].includes(
          productSchedulingMode,
        )
      })

      return (
        productWithDifferentSchedulingMode?.scheduling?.mode ??
        SCHEDULING_MODE_OPTIONAL
      )
    },

    getAllowedPortion({ portion, size }) {
      const sizePortions = size?.allowedPortions ?? []

      return sizePortions.find(({ id }) => id === portion?.id)
    },

    getItemProductsRange({ portion, size, sizePortion }) {
      if (!sizePortion && portion) {
        const sizePortions = size?.portions ?? size?.allowedPortions ?? []

        sizePortion = sizePortions.find(({ id }) => id === portion.id)
      }

      return {
        min:
          sizePortion?.minProducts ??
          size?.minProducts ??
          portion?.minProducts ??
          1,
        max:
          sizePortion?.maxProducts ??
          size?.maxProducts ??
          portion?.maxProducts ??
          1,
      }
    },

    getNumberProductsText({ numberProductsRange, section }) {
      const { max, min } = numberProductsRange

      let text = ''

      if (min === max) text += max
      else if (min > 1) text += `${min} a ${max}`
      else if (min === 1) text += `Até ${max}`

      if (section.type === SECTION_TYPE_PIZZA)
        text += ` sabor${max > 1 ? 'es' : ''}`
      else text += ` produto${max > 1 ? 's' : ''}`

      return text
    },

    getItemProductsPrice({ item, fieldName = 'price' }) {
      const { section, portion, size, products } = item
      const totalUniqueProducts = [
        ...new Set(products.map(product => product.id)),
      ].length
      const allowedPortion = app.getAllowedPortion({ portion, size })
      const priceMode =
        allowedPortion?.priceMode ??
        allowedPortion?.priceType ??
        PRICE_TYPE_AVERAGE

      let productsPrice = products.map(product =>
        app.getPrice({
          price: product[fieldName],
          portion,
          size,
          numberProducts: totalUniqueProducts,
        }),
      )

      switch (priceMode) {
        case PRICE_TYPE_MORE_EXPENSIVE:
          productsPrice = Math.max(...productsPrice)
          break

        case PRICE_TYPE_CHEAPER:
          productsPrice = Math.min(...productsPrice)
          break

        default:
          productsPrice =
            productsPrice.reduce((acc, price) => acc + price, 0) /
            productsPrice.length
          break
      }

      const sectionRoundPriceMode =
        section?.roundPriceMode ?? section?.roundPrice

      switch (sectionRoundPriceMode) {
        case ROUND_PRICE_UP:
          productsPrice = Math.ceil(productsPrice)
          break

        case ROUND_PRICE_DOWN:
          productsPrice = Math.floor(productsPrice)
          break
      }

      return productsPrice
    },

    getItemBasePrice({ item, fieldName = 'price', ignoreCache = false }) {
      if (ignoreCache === false && item.basePrice !== undefined)
        return item.basePrice

      const productsTotalPrice = app.getItemProductsPrice({ item, fieldName })
      const extras = app.getItemExtras(item)
      const extrasTotalPrice = extras.reduce(
        (acc1, extra) =>
          acc1 + app.getExtraTotalPrice({ extra, item, fieldName }),
        0,
      )

      return productsTotalPrice + extrasTotalPrice
    },

    getItemTotalPrice({
      item,
      price,
      fieldName = 'price',
      ignoreCache = false,
    }) {
      let totalPrice

      if (price !== undefined) totalPrice = price
      else totalPrice = app.getItemBasePrice({ item, fieldName, ignoreCache })

      totalPrice *= item.quantity

      return totalPrice
    },

    getItemExtras(item) {
      const { section } = item
      const sectionExtras = section?.extras ?? []
      const productsExtras = item.products.flatMap(product => {
        const productExtras = product?.extras ?? []
        const categoryExtras = product?.category?.extras ?? []

        return [...productExtras, ...categoryExtras]
      })
      const extras = [...sectionExtras, ...productsExtras]

      return extras
    },

    getPrice({ price, portion, size, numberProducts = 1 }) {
      if (is_numeric(price) === true) return Number.parseFloat(price)
      else {
        const portionId = portion?.id

        let priceAux

        if (
          portionId &&
          (priceAux = price?.portions?.[portionId]) !== undefined
        ) {
          price = priceAux
        } else {
          const sizeId = size?.id

          if (sizeId && (priceAux = price?.sizes?.[sizeId]) !== undefined) {
            price = priceAux
          } else if (
            Number.isInteger(numberProducts) &&
            (priceAux = price?.numberProducts?.[numberProducts]) !== undefined
          ) {
            price = priceAux
          } else return undefined
        }
      }

      return this.getPrice({
        price,
        portion,
        size,
        numberProducts,
      })
    },

    getMaskedCardNumber(cardNumber) {
      return `**** **** **** ${cardNumber.toString().substr(-4)}`
    },

    async enableIntentEvent() {
      if (app.platform === 'App') {
        const intentShim = window.plugins?.intentShim

        if (intentShim)
          intentShim.onIntent(intent => {
            app.$emit('newIntent', intent)
          })
      }
    },

    // async connectSocket() {
    //   const socket = app.socket

    //   socket.on('connect', async () => {
    //     let data = {
    //       platform: app.platform,
    //       version: app.version,
    //       path: window.location.href,
    //     }

    //     if (app.hasLoggedUser() === true) data.userId = app.user.id

    //     socket.emit('setConnectedUser', data)
    //   })
    // },

    async enableDeviceReadyFallback() {
      if (app.platform !== 'App') {
        this.log.info('Adding deviceready fallback...')

        const bkAddEventListener = document.addEventListener

        document.addEventListener = (eventName, handler, capture) => {
          if (eventName === 'deviceready' && typeof handler === 'function')
            handler()
          else bkAddEventListener.call(document, eventName, handler, capture)
        }

        this.log.success('Deviceready fallback added!')
      }
    },

    async getStorageInstance() {
      const instance = (window.storageInstance =
        window.storageInstance ?? localForage)

      if (instance?.supports?.('cordovaSQLiteDriver') === true)
        await this.installSQLiteDriver(instance)

      return instance
    },

    async installSQLiteDriver(instance) {
      try {
        const sqliteDriver = await import('localforage-cordovasqlitedriver')

        if (instance.supports(sqliteDriver._driver) === true)
          this.log.info('SQLite already installed!')
        else {
          this.log.info('Installing SQLite driver...')

          await instance.defineDriver(sqliteDriver)

          instance.setDriver([
            sqliteDriver._driver,
            instance.INDEXEDDB,
            instance.WEBSQL,
            instance.LOCALSTORAGE,
          ])

          this.log.success('SQLite driver installed successfully!')
        }

        return true
      } catch (e) {
        this.log.error('Could not install SQLite driver!', e)
        return false
      }
    },

    async removeItem(key) {
      const storage = await this.getStorageInstance()

      return storage.removeItem(key)
    },

    async getItems() {
      const storageInstance = await this.getStorageInstance()
      const items = {}

      await storageInstance.iterate((value, key) => {
        items[key] = value
      })

      return items
    },

    async getItem(key) {
      const storage = await this.getStorageInstance()

      return storage.getItem(key)
    },

    async setItem(key, value) {
      if (value === undefined) return this.removeItem(key)

      const storage = await this.getStorageInstance()

      return storage.setItem(key, value)
    },

    async setItems(data = {}) {
      const promises = Object.keys(data).map(async key => {
        const value = data[key]

        return this.setItem(key, value)
      })

      return Promise.all(promises)
    },

    async requestUserCpf({ user = app.user, description } = {}) {
      return new Promise(async (resolve, reject) => {
        const { default: UserRequestCpf } = await import(
          '@/components/User/RequestCpf.vue'
        )
        const { modal, component } = app.getModalComponent({
          component: UserRequestCpf,
          props: {
            description,
            user,
          },
        })

        component
          .$on('saved', updatedUser => {
            updateObject(user, updatedUser)
            app.user = updatedUser

            modal.close()
            resolve(updatedUser.cpf)
          })
          .$on('cancel', modal.close)

        modal.$on('closed', () => {
          component.$destroy()
          modal.$destroy()
          reject()
        })

        modal.open()
      })
    },

    instanceComponent({ component, props = {}, ...options }) {
      component = markRaw(component)

      const app = createApp(component, props)

      if (options) {
        Object.assign(app.config.globalProperties, options)
      }

      const container = document.createElement('div')
      const instance = app.mount(container)

      return instance
    },

    getModalComponent({ modalNoPadding = true, ...options } = {}) {
      const modal = app.instanceComponent({
        component: AppModal,
        props: {
          noPadding: modalNoPadding,
        },
        parent: this,
      })
      const component = app.instanceComponent({ ...options, parent: modal })

      modal.$el.children[0].appendChild(component.$el)

      return {
        modal,
        component,
      }
    },

    async openComponentInDrawer(component, { props, drawerProps } = {}) {
      const { drawers } = this
      const drawerIndex =
        drawers.push({
          component: markRaw(component),
          props,
          drawerProps,
        }) - 1

      await this.$nextTick()

      const drawerKey = `drawer-${drawerIndex}`
      const drawerInstance = this.$refs[drawerKey]?.[0]

      drawerInstance.$on('closed', () => {
        drawers.splice(drawerIndex, 1)
      })

      await drawerInstance.open()

      const childInstance = this.$refs[`${drawerKey}-component`]?.[0]

      return {
        instances: {
          drawer: drawerInstance,
          component: childInstance,
        },
      }
    },

    async onNewIntent(intent) {
      const intentRoute = await this.getIntentRoute(intent)

      if (intentRoute) app.goToScreen(intentRoute)
    },

    getFormattedPercentage(value) {
      if (typeof value === 'number') {
        value = app.truncateToDecimals(value, 2)
        value /= 100
        value = value.toPrecision(4)
      }

      return new Intl.NumberFormat(undefined, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value)
    },

    getPaymentCardImage(cardNumber) {
      const cardBrand = app.getPaymentCardBrand(cardNumber)
      const cardBrandSlug = slug(cardBrand, { lower: true })

      let fileName = `${cardBrandSlug}.svg`

      return `https://assets.deliveryinbox.com.br/images/payment-forms/${fileName}`
    },

    getPaymentCardBrand(cardNumber) {
      return CardValidator.number(cardNumber).card.niceType
    },

    async getPaymentCardHash(
      { number, holderName, expiry, securityCode },
      { attempt = 1, maxRetries = 3 } = {},
    ) {
      try {
        const {
          keys: { encryption: encryptionKey },
        } = pagarmeConfigs
        const client = await pagarme.client.connect({
          encryption_key: encryptionKey,
        })
        const paymentCardHash = await client.security.encrypt({
          card_number: number,
          card_holder_name: holderName,
          card_expiration_date: `${expiry.month}${expiry.year}`,
          card_cvv: securityCode,
        })

        return paymentCardHash
      } catch (e) {
        console.error(e)

        if (attempt <= maxRetries)
          return this.getPaymentCardHash(arguments[0], {
            ...(arguments?.[1] ?? {}),
            attempt: attempt + 1,
          })
      }
    },

    async getPaymentCardFromId(paymentCardId, user = app.user) {
      const paymentCards = await app.getPaymentCards({ user })

      return paymentCards.find(({ id }) => id === paymentCardId)
    },

    getPaymentCardsStorageKey({ id } = {}) {
      if (!id) return

      return `cards-${id}`
    },

    async getPaymentCardsData(user = app.user) {
      const storageKey = this.getPaymentCardsStorageKey(user)
      const data = await app.getItem(storageKey)

      return data
    },

    async getPaymentCards({ user = app.user } = {}) {
      const userId = user?.id
      const encryptedString = await this.getPaymentCardsData(user)

      let paymentCards = []

      if (encryptedString) {
        const encryptKey = userId
        const decryptedString = app.decrypt(encryptedString, encryptKey)

        try {
          paymentCards = atob(decryptedString)
        } catch (e) {
          console.error(e)
          paymentCards = decryptedString
        }

        paymentCards = JSON.parse(paymentCards)
      }

      paymentCards = paymentCards
        .map(this.getNormalizedPaymentCard)
        .filter(this.isValidPaymentCard)

      return paymentCards
    },

    getNormalizedPaymentCard(paymentCard) {
      return {
        ...(paymentCard ?? {}),
        number: getOnlyNumbers(paymentCard.number),
        holderName: sanitizePaymentCardHolderName(paymentCard.holderName),
        expiry: getPaymentCardExpiry(paymentCard.expiry),
      }
    },

    isValidPaymentCard(paymentCard) {
      const numberValidatorResult = validatePaymentCardNumber(
        paymentCard?.number,
      )

      if (numberValidatorResult !== true) return false

      const holderName = sanitizePaymentCardHolderName(paymentCard?.holderName)
      const holderNameValidatorResult = cardholderName(holderName)

      if (holderName.length < 3) return false
      else if (holderNameValidatorResult.isValid === false) return false

      const expiryValidatorResult = expirationDate(paymentCard?.expiry)

      if (expiryValidatorResult.isValid === false) return false

      return true
    },

    async savePaymentCards({ paymentCards = [], user = app.user }) {
      const userId = user?.id
      const storageKey = this.getPaymentCardsStorageKey(user)

      if (paymentCards.length === 0) return app.removeItem(storageKey)

      const encryptKey = userId

      let data = paymentCards

      data = JSON.stringify(data)
      data = btoa(data)
      data = app.encrypt(data, encryptKey)

      return app.setItem(storageKey, data)
    },

    async addPaymentCard({ user, paymentCard }) {
      const paymentCards = await app.getPaymentCards({ user })

      if (paymentCards.some(({ id }) => id === paymentCard.id))
        throw 'PAYMENT_CARD_ALREADY_EXISTS'

      paymentCards.push(paymentCard)

      return app.savePaymentCards({ user, paymentCards })
    },

    encrypt(value, key) {
      if (typeof value !== 'string') value = JSON.stringify(value)

      const cipher = crypto.createCipher('aes-128-ctr', key)

      let encrypted = cipher.update(value, 'utf8', 'hex')
      encrypted += cipher.final('hex')

      return encrypted
    },

    decrypt(encryptedString, key) {
      const decipher = crypto.createDecipher('aes-128-ctr', key)

      let decrypted = decipher.update(encryptedString, 'hex', 'utf8')
      decrypted += decipher.final('utf8')

      return decrypted
    },

    getTotalOrderPrice({
      order,
      includePaymentForm = true,
      includeDeliveryWay = true,
      includeItems = true,
      includeCashback = true,
      includeMods = true,
    }) {
      const priceMods = order.priceMods ?? []
      const runMod = mod => {
        const allowNegative = mod?.allowNegative ?? false

        let modResult = calculate(orderPrice, mod.value)

        if (modResult < 0 && allowNegative === false) modResult = 0

        orderPrice = modResult
      }

      let orderPrice = 0

      if (includeItems === true)
        orderPrice += order.items.reduce(
          (acc, item) => acc + app.getItemTotalPrice({ item }),
          0,
        )

      if (includeMods === true)
        priceMods
          .filter(
            mod =>
              mod.includeDeliveryWay !== true &&
              (mod.includeCashback ?? mod.includePoints) !== true,
          )
          .forEach(runMod)

      if (includeDeliveryWay === true)
        orderPrice += app.getDeliveryPrice(order) || 0

      if (includeMods === true)
        priceMods.filter(mod => mod.includeDeliveryWay === true).forEach(runMod)

      if (includeCashback === true) orderPrice -= order?.cashback?.discount ?? 0

      if (includeMods === true)
        priceMods
          .filter(mod => mod.includeCashback ?? mod.includePoints === true)
          .forEach(runMod)

      return orderPrice
    },

    async getPointPrice() {
      const timestampToCompare = app.pointPrice?.timestamp
      const currentTimestamp = Date.now()
      const difference = Math.abs(currentTimestamp - timestampToCompare) / 1000

      if (difference <= 60) return app.pointPrice?.price

      const response = await app.callApi({
        method: 'GET',
        action: 'me/points',
      })
      const pointPrice = response?.data?.pointPrice

      app.pointPrice = {
        price: pointPrice,
        timestamp: currentTimestamp,
      }

      return pointPrice
    },

    async getPointsToPrice({ points, pointPrice }) {
      if (!pointPrice) pointPrice = await app.getPointPrice()

      let price = points * pointPrice

      price = price.toFixed(2)
      price = parseFloat(price)

      return price
    },

    async getPriceToPoints({ price, pointPrice }) {
      if (!pointPrice) pointPrice = await app.getPointPrice()

      return Math.ceil(price / pointPrice) | 0
    },

    async getDeliveryPrice(order, companyComponent) {
      order = normalizeOrder(order)

      await companyComponent.processRules({
        order,
        lockMultipleProcess: false,
        ignoreEmits: true,
      })

      const {
        totalDeliveryFeeModifications = 0,
        deliveryWay,
        deliveryAddress,
      } = order

      if (!deliveryWay) return

      let deliveryPrice = deliveryWay.basePrice ?? 0

      if (deliveryWay.requireAddress === true) {
        if (!deliveryAddress) return

        let { city, neighborhood } = deliveryAddress

        if (city) {
          const deliveryWayCities =
            deliveryWay?.cities ?? deliveryWay?.allowedCities ?? []

          city = deliveryWayCities.find(({ id }) => id === city.id)

          if (!city || city?.companyDeliveryWayCity?.active === false) return

          deliveryPrice = calculate(
            deliveryPrice,
            city?.companyDeliveryWayCity?.deliveryPrice ??
              city?.companyDeliveryWayCity?.price,
          )
        }

        if (neighborhood) {
          const deliveryWayNeighborhoods = deliveryWay?.neighborhoods ?? []

          neighborhood = deliveryWayNeighborhoods.find(
            ({ id }) => id === neighborhood.id,
          )

          if (neighborhood?.companyDeliveryWayNeighborhood?.active === false)
            return

          deliveryPrice = calculate(
            deliveryPrice,
            neighborhood?.companyDeliveryWayNeighborhood?.deliveryPrice ??
              neighborhood?.companyDeliveryWayNeighborhood?.price,
          )
        }
      }

      if (deliveryWay.totalPrice !== undefined)
        deliveryPrice = calculate(deliveryPrice, deliveryWay.totalPrice)

      if (deliveryWay.price !== undefined)
        deliveryPrice = calculate(deliveryPrice, deliveryWay.price)

      const oldPrice = deliveryPrice

      deliveryPrice += totalDeliveryFeeModifications

      return [oldPrice, deliveryPrice]
    },

    // async checkUpdate() {
    // return new Promise((resolve, reject) => {
    //   try {
    //     const checkUpdateFunction = window.AppUpdate?.needsUpdate
    //     if (typeof checkUpdateFunction !== 'function') return resolve()
    //     checkUpdateFunction(
    //       ({ update_available: updateAvailable }) =>
    //         resolve(updateAvailable == true),
    //       reject,
    //     )
    //   } catch (e) {
    //     reject(e)
    //   }
    // })
    // },

    truncateToDecimals(num, dec = 2) {
      const calcDec = Math.pow(10, dec)

      return Math.trunc(num * calcDec) / calcDec
    },

    getDeliveryWayDeliveryTimeRange({ deliveryWay, deliveryTime } = {}) {
      return this.getDeliveryTimeRange(deliveryTime, {
        deliveryWay,
      })
    },

    getCompanyDeliveryTimeRange({ deliveryTime } = {}) {
      return this.getDeliveryTimeRange(deliveryTime)
    },

    getActiveDeliveryTimeRules(rules = []) {
      return rules
        .filter(defaultFilterFunction)
        .filter(({ active }) => active === true)
    },

    getDeliveryTimeRange(
      { mode = DELIVERY_TIME_MODE_LONGEST_TIME, rules = [] } = {},
      filters = {},
    ) {
      const arraySum = array => array.reduce((acc, item) => acc + item, 0)
      const deliveryTimeMode = mode ?? DELIVERY_TIME_MODE_LONGEST_TIME

      let min = []
      let max = []
      let filteredRules = this.getActiveDeliveryTimeRules(rules)

      if (filters.section)
        filteredRules = filteredRules.filter(
          ({ type, value }) =>
            type === DELIVERY_TIME_RULE_TYPE_SECTION &&
            value === filters.section.id,
        )

      if (filters.category)
        filteredRules = [
          filteredRules.find(
            ({ type, value }) =>
              type === DELIVERY_TIME_RULE_TYPE_CATEGORY &&
              value === filters.category?.id,
          ) ??
            filteredRules.find(
              ({ type, value }) =>
                type === DELIVERY_TIME_RULE_TYPE_SECTION &&
                value ===
                  (filters.category?.section?.id ??
                    filters.category?.sectionId),
            ),
        ]

      if (filters.product)
        filteredRules = [
          filteredRules.find(
            ({ type, value }) =>
              type === DELIVERY_TIME_RULE_TYPE_PRODUCT &&
              value === filters.product.id,
          ) ??
            filteredRules.find(
              ({ type, value }) =>
                type === DELIVERY_TIME_RULE_TYPE_CATEGORY &&
                value ===
                  (filters.product?.category?.id ??
                    filters.product?.categoryId),
            ) ??
            filteredRules.find(
              ({ type, value }) =>
                type === DELIVERY_TIME_RULE_TYPE_SECTION &&
                value ===
                  (filters.product?.section?.id ?? filters.product?.sectionId),
            ),
        ]

      filteredRules = filteredRules.filter(e => e)

      if (filteredRules.length === 0)
        filteredRules = rules.filter(
          ({ type }) => type === DELIVERY_TIME_RULE_TYPE_GENERAL,
        )

      filteredRules.forEach(({ deliveryWays }) => {
        if (filters.deliveryWay)
          deliveryWays = deliveryWays.filter(
            ({ id }) => id === filters.deliveryWay.id,
          )

        deliveryWays.forEach(({ minTime, maxTime }) => {
          min.push(minTime)
          max.push(maxTime)
        })
      })

      switch (deliveryTimeMode) {
        case DELIVERY_TIME_MODE_LONGEST_TIME:
          min = Math.min(...min)
          max = Math.max(...max)
          break

        case DELIVERY_TIME_MODE_SHORTEST_TIME:
          min = Math.min(...min)
          max = Math.min(...max)
          break

        case DELIVERY_TIME_MODE_SUM_TIMES:
          min = arraySum(min)
          max = arraySum(max)
          break

        case DELIVERY_TIME_MODE_AVERAGE_TIMES:
          const arrayAvg = array => arraySum(array) / array.length

          min = arrayAvg(min)
          max = arrayAvg(max)
          break
      }

      return {
        min: Math.ceil(min),
        max: Math.ceil(max),
      }
    },

    async loadCordova() {
      return new Promise(async resolve => {
        try {
          const needCordova = THE_TRUE_ARRAY.includes(
            import.meta.env.VITE_LOAD_CORDOVA ??
              import.meta.env.LOAD_CORDOVA ??
              this.$route.query.loadCordova ??
              (window.location.protocol === 'ionic:' ||
                window.location.href.includes('loadCordova=true') ||
                '_cordovaNative' in window),
          )

          if (needCordova === false) {
            this.log.info('It is not necessary to load cordova.js!')

            return resolve(true)
          }

          this.platform = 'App'

          if (window.cordova) {
            this.log.info('Cordova has already been loaded!')

            return resolve(true)
          }

          this.log.info('Loading cordova.js...')

          const os = app.os?.toLowerCase?.() === 'android' ? 'android' : 'ios'
          const checksumHash =
            import.meta.env.VITE_CORDOVA_CHECKSUM_HASH ??
            import.meta.env.CORDOVA_CHECKSUM_HASH
          const cordovaScriptUrl = `cordova/platforms/${os}/${checksumHash}/cordova.js`

          await importScript(cordovaScriptUrl)

          document.addEventListener(
            'deviceready',
            () => {
              this.log.info('Cordova.js loaded!')

              resolve(true)
            },
            false,
          )
        } catch (e) {
          console.error(e)
          this.log.error('Error in loading cordova.js!', e)
          return resolve(false)
        }
      })
    },

    getCurrentRouteMatch() {
      const matchedRoutes = app.$route.matched

      return matchedRoutes[matchedRoutes.length - 1]
    },

    getCurrentRouteComponent() {
      const matchRoute = app.getCurrentRouteMatch()

      return matchRoute.components?.default
    },

    getCurrentRouteInstance() {
      const matchRoute = app.getCurrentRouteMatch()

      return matchRoute.instances.default
    },

    productToItem({ product }) {
      product = clone(product)

      delete product.loading
      delete product.unitValue

      let item = {
        observations: undefined,
        quantity: 1,
        numberProducts: 1,
        portion: { id: undefined },
        size: { id: undefined },
        products: [product],
      }

      return item
    },

    isSectionMultiProduct(section) {
      const portions = section?.portions ?? []
      const sizes = section?.sizes ?? []

      return (
        portions.some(portion => portion?.maxProducts > 1) ||
        sizes.some(
          size =>
            size?.maxProducts > 1 ||
            app
              .getSizeAllowedPortions(size)
              .some(portion => portion?.maxProducts > 1),
        ) ||
        false
      )
    },

    async isItemMultiProduct({ section }) {
      return this.isSectionMultiProduct(section)
    },

    async getItemSection(item) {
      if (item?.section) return item.section
      else {
        const sectionId = item?.products?.[0]?.section?.id

        if (sectionId)
          return await app.getSection({
            sectionId,
          })
      }
    },

    getGeneralDeliveryTimeRule({ company, active = true }) {
      return company.deliveryTime.rules.find(
        rule =>
          rule.type === DELIVERY_TIME_RULE_TYPE_GENERAL &&
          rule.active === active,
      )
    },

    getOperationalDeliveryTimeRules(establishment, active = true) {
      return establishment.deliveryTime.rules.filter(
        rule =>
          rule.type === DELIVERY_TIME_RULE_TYPE_OPERATIONAL &&
          rule.active === active,
      )
    },

    getSectionDeliveryTimeRule({ section, company, active = true }) {
      return company.deliveryTime.rules.find(
        rule =>
          rule.type === DELIVERY_TIME_RULE_TYPE_SECTION &&
          rule.value == section.id &&
          rule.active === active,
      )
    },

    getCategoryDeliveryTimeRule(categoryId, establishment, active = true) {
      return establishment.deliveryTime.rules.find(
        rule =>
          rule.type === DELIVERY_TIME_RULE_TYPE_CATEGORY &&
          rule.value == categoryId &&
          rule.active === active,
      )
    },

    getProductDeliveryTimeRule(productId, establishment, active = true) {
      return establishment.deliveryTime.rules.find(
        rule =>
          rule.type === DELIVERY_TIME_RULE_TYPE_PRODUCT &&
          rule.value == productId &&
          rule.active === active,
      )
    },

    getDeliveryTimeRuleDeliveryWays({ rule, company }) {
      const deliveryWays = []
      const companyDeliveryWays = company?.deliveryWays ?? []

      rule.deliveryWays.forEach(ruleDeliveryWay => {
        const ruleDeliveryWayId =
          ruleDeliveryWay.id ?? ruleDeliveryWay.deliveryWayId
        const companyDeliveryWay =
          companyDeliveryWays.find(({ id }) => id === ruleDeliveryWayId) ?? {}

        deliveryWays.push({
          ...companyDeliveryWay,
          deliveryTime: {
            min: ruleDeliveryWay.minTime,
            max: ruleDeliveryWay.maxTime,
          },
        })
      })

      return deliveryWays
    },

    // getDeliveryTimeRange({ deliveryWays }) {
    //   let min = []
    //   let max = []

    //   deliveryWays.forEach(deliveryWay => {
    //     min.push(deliveryWay?.deliveryTime?.min)
    //     max.push(deliveryWay?.deliveryTime?.max)
    //   })

    //   min = Math.min(...min)
    //   max = Math.max(...max)

    //   return {
    //     min,
    //     max,
    //   }
    // },

    getDeliveryTime({
      deliveryWay,
      order,
      item,
      product,
      category,
      section,
      company,
    }) {
      if (deliveryWay)
        return app.getDeliveryWayDeliveryTime({ deliveryWay, company })
      if (product) return app.getProductDeliveryTime({ product, company })
      if (section) return app.getSectionDeliveryTime({ section, company })
      if (item) return app.getItemDeliveryTime({ item, company })
      if (category) return app.getCategoryDeliveryTime({ category, company })
      if (order) return app.getOrderDeliveryTime(order)
      if (company) return app.getCompanyDeliveryTime({ company })
    },

    getDeliveryTimeRule({
      product,
      category,
      section,
      company,
      active = true,
    }) {
      const companyDeliveryTimeRules = company?.deliveryTime?.rules ?? []

      let ruleType = DELIVERY_TIME_RULE_TYPE_GENERAL
      let ruleValue

      if (product) {
        ruleType = DELIVERY_TIME_RULE_TYPE_PRODUCT
        ruleValue = product.id
      } else if (category) {
        ruleType = DELIVERY_TIME_RULE_TYPE_CATEGORY
        ruleValue = category.id
      } else if (section) {
        ruleType = DELIVERY_TIME_RULE_TYPE_SECTION
        ruleValue = section.id
      }

      return (
        companyDeliveryTimeRules.find(rule => {
          if (rule.type !== ruleType) return false
          if (typeof active === 'boolean' && rule.active !== active)
            return false
          if (ruleValue !== undefined && rule.value != ruleValue) return false

          return true
        }) ?? this.getGeneralDeliveryTimeRule({ company })
      )
    },

    getCompanyDeliveryTime({ company }) {
      const rule = this.getDeliveryTimeRule({ company })
      const companyDeliveryTimeRange = this.getCompanyDeliveryTimeRange(company)

      return {
        ...rule,
        deliveryWays: [{ deliveryTime: companyDeliveryTimeRange }],
      }
    },

    getSectionDeliveryTime({ section, company }) {
      const rule =
        this.getDeliveryTimeRule({ section, company }) ||
        this.getDeliveryTimeRule({ company })

      return {
        ...rule,
        deliveryWays: this.getDeliveryTimeRuleDeliveryWays({
          rule,
          company,
        }),
      }
    },

    getCategoryDeliveryTime({ category, company }) {
      const rule =
        this.getDeliveryTimeRule({ category, company }) ||
        this.getDeliveryTimeRule({ section: category.section, company }) ||
        this.getDeliveryTimeRule({ company })

      return {
        ...rule,
        deliveryWays: this.getDeliveryTimeRuleDeliveryWays({
          rule,
          company,
        }),
      }
    },

    getDeliveryWayDeliveryTime({ deliveryWay, company }) {
      const rules = (company?.deliveryTime?.rules ?? [])
        .filter(defaultFilterFunction)
        .filter(({ active }) => active === true)

      return {
        ...rule,
        deliveryWays: this.getDeliveryTimeRuleDeliveryWays({
          rule,
          company,
        }),
      }
    },

    getProductDeliveryTime({ product, company }) {
      const rule =
        this.getDeliveryTimeRule({ product, company }) ??
        this.getDeliveryTimeRule({ category: product.category, company }) ??
        this.getDeliveryTimeRule({ section: product.section, company }) ??
        this.getDeliveryTimeRule({ company })

      return {
        ...rule,
        deliveryWays: this.getDeliveryTimeRuleDeliveryWays({
          rule,
          company,
        }),
      }
    },

    getRuleWithOperational(rule, establishment) {
      const operationalRules =
        app.getOperationalDeliveryTimeRules(establishment)

      operationalRules.forEach(operationalRule => {
        operationalRule.deliveryWays.forEach(operationalRuleDeliveryWay => {
          const ruleDeliveryWayId =
            operationalRuleDeliveryWay.id ??
            operationalRuleDeliveryWay.deliveryWayId
          const ruleDeliveryWay = app.getDeliveryWayFromRule(
            ruleDeliveryWayId,
            rule,
          )

          ruleDeliveryWay.minTime += operationalRuleDeliveryWay.minTime
          ruleDeliveryWay.maxTime += operationalRuleDeliveryWay.maxTime
        })
      })

      return rule
    },

    getItemDeliveryTime({ item, company }) {
      const itemDeliveryTime = {
        deliveryWays: [],
      }

      item.products.forEach(product => {
        const productDeliveryTime = app.getProductDeliveryTime({
          product,
          company,
        })

        productDeliveryTime.deliveryWays.forEach(deliveryWay => {
          let itemDeliveryWay = app.getDeliveryWayFromRule(
            deliveryWay.id,
            itemDeliveryTime,
          )

          if (!itemDeliveryWay) {
            itemDeliveryWay = {
              id: deliveryWay.id,
              name: deliveryWay.name,
              active: deliveryWay.active,
              requireAddress: deliveryWay.requireAddress,
              deliveryTime: {
                min: [],
                max: [],
              },
            }

            itemDeliveryTime.deliveryWays.push(itemDeliveryWay)
          }

          itemDeliveryWay.deliveryTime.min.push(deliveryWay.deliveryTime.min)
          itemDeliveryWay.deliveryTime.max.push(deliveryWay.deliveryTime.max)
        })
      })

      itemDeliveryTime.deliveryWays.forEach(itemDeliveryWay => {
        switch (company.deliveryTime.mode) {
          case DELIVERY_TIME_MODE_LONGEST_TIME:
            itemDeliveryWay.deliveryTime.min = Math.max(
              ...itemDeliveryWay.deliveryTime.min,
            )
            itemDeliveryWay.deliveryTime.max = Math.max(
              ...itemDeliveryWay.deliveryTime.max,
            )
            break

          case DELIVERY_TIME_MODE_SUM_TIMES:
            itemDeliveryWay.deliveryTime.min =
              itemDeliveryWay.deliveryTime.min.reduce((a, b) => a + b, 0)
            itemDeliveryWay.deliveryTime.max =
              itemDeliveryWay.deliveryTime.max.reduce((a, b) => a + b, 0)
            break

          case DELIVERY_TIME_MODE_AVERAGE_TIMES:
            const minLength = itemDeliveryWay.deliveryTime.min.length
            const maxLength = itemDeliveryWay.deliveryTime.max.length

            itemDeliveryWay.deliveryTime.min =
              itemDeliveryWay.deliveryTime.min.reduce((a, b) => a + b, 0) /
              minLength
            itemDeliveryWay.deliveryTime.max =
              itemDeliveryWay.deliveryTime.max.reduce((a, b) => a + b, 0) /
              maxLength
            break

          default:
            app.log.error(
              'Não foi possível determinar o modo de cálculo do tempo de entrega!',
            )
            break
        }
      })

      return itemDeliveryTime
    },

    getOrderDeliveryTime({ items, company, deliveryWay }) {
      const deliveryWayId = deliveryWay?.id
      const operationalRules = app.getOperationalDeliveryTimeRules(company)

      let minTime = []
      let maxTime = []

      items.forEach(item => {
        const orderItemRule = app.getItemDeliveryTime({
          item,
          company,
        })
        const ruleDeliveryWay = app.getDeliveryWayFromRule(
          deliveryWayId,
          orderItemRule,
        )

        if (ruleDeliveryWay) {
          const min =
            ruleDeliveryWay?.deliveryTime?.min ?? ruleDeliveryWay?.minTime
          const max =
            ruleDeliveryWay?.deliveryTime?.max ?? ruleDeliveryWay?.maxTime

          minTime.push(min)
          maxTime.push(max)
        }
      })

      switch (company.deliveryTime.mode) {
        case DELIVERY_TIME_MODE_SUM_TIMES:
          minTime = minTime.reduce((a, b) => a + b, 0)
          maxTime = maxTime.reduce((a, b) => a + b, 0)
          break

        case DELIVERY_TIME_MODE_AVERAGE_TIMES:
          minTime = minTime.reduce((a, b) => a + b, 0) / minTime.length
          maxTime = maxTime.reduce((a, b) => a + b, 0) / maxTime.length
          break

        case DELIVERY_TIME_MODE_LONGEST_TIME:
          minTime = Math.max(...minTime)
          maxTime = Math.max(...maxTime)
          break

        case DELIVERY_TIME_MODE_SHORTEST_TIME:
          minTime = Math.min(...minTime)
          maxTime = Math.min(...maxTime)
          break
      }

      operationalRules.forEach(operationalRule => {
        const ruleDeliveryWay = app.getDeliveryWayFromRule(
          deliveryWayId,
          operationalRule,
        )

        minTime += ruleDeliveryWay.minTime
        maxTime += ruleDeliveryWay.maxTime
      })

      return {
        minTime,
        maxTime,
      }
    },

    getDeliveryWayFromId(deliveryWayId, deliveryWays = []) {
      return deliveryWays.find(({ id }) => id === deliveryWayId)
    },

    getDeliveryWayFromRule(deliveryWayId, rule = {}) {
      return rule.deliveryWays.find(
        ruleDeliveryWay =>
          (ruleDeliveryWay.id ?? ruleDeliveryWay.deliveryWayId) ===
          deliveryWayId,
      )
    },

    getDeliveryTimeText({ min, max, small = true } = {}) {
      if (!min && max) {
        if (small) return `Até ${max} min`
        else return `Até ${max} minuto${max === 1 ? '' : 's'}`
      } else if (min && !max) {
        if (small) return `No mínimo ${max} min`
        else return `No mínimo ${min} minuto${min === 1 ? '' : 's'}`
      } else if (min !== max) {
        if (small) return `${min}-${max} min`
        else return `Entre ${min} e ${max} minuto${max === 1 ? '' : 's'}`
      } else if (min === max && max !== null) {
        if (small) return `${max} min`
        else return `${max} minuto${max === 1 ? '' : 's'}`
      }
    },

    async checkGeolocation() {
      this.log.info('checkGeolocation called!')

      if (this.platform !== 'App') {
        if (navigator.permissions) {
          navigator.permissions
            .query({ name: 'geolocation' })
            .then(permissionStatus => {
              let geolocationAlert

              switch (permissionStatus.state) {
                case 'prompt':
                  this.log.info('Requesting geolocation permission...')

                  geolocationAlert = app.alert({
                    title: 'GPS',
                    content:
                      'Permita que o Delivery In Box acesse a sua localização para <b>facilitar a entrega do seu pedido</b>.',
                  })

                  app.getCurrentPosition().then(geolocationAlert.close)
                  break

                case 'granted':
                  this.log.success('Geolocation granted!')

                  if (geolocationAlert) geolocationAlert.close()

                  break

                case 'denied':
                  this.log.error('Geolocation denied!')

                  // geolocationAlert = app.alert({
                  //   title: 'GPS',
                  //   content:
                  //     'O Delivery In Box não conseguirá determinar a sua localização pois <b>você não permitiu o acesso ao GPS</b>. Permita a localização para uma melhor experiência ;)',
                  // })
                  break
              }

              permissionStatus.onchange = app.checkGeolocation
            })
        } else {
          app.getCurrentPosition()
        }
      }
    },

    async getCurrentPosition(enableHighAccuracy = true, timeout = 15 * 1000) {
      return new Promise((resolve, reject) => {
        document.addEventListener(
          'deviceready',
          () =>
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout,
              enableHighAccuracy,
            }),
          false,
        )
      })
    },

    async loadGoogleTagManager(pageview = true) {
      this.log.info('Loading Google Tag Manager...')

      $app.use(
        createGtm({
          id: Configs.Google.TagManager.id,
          debug: app.environment === 'development',
        }),
      )

      this.$router.afterEach(({ fullPath }) => this.trackPageview(fullPath))

      if (pageview === true) this.trackPageview()

      return true
    },

    async getGoogleAnalyticsClientId() {
      const storageKey = 'gaId'

      return (
        window.googleAnalyticsClientId ??
        (await app.getItem(storageKey)) ??
        new Promise(async (resolve, reject) => {
          try {
            const ga = await this.getGoogleAnalytics()

            ga(async tracker => {
              const clientId = tracker.get('clientId')

              await app.setItem(storageKey, clientId)

              window.googleAnalyticsClientId = clientId

              resolve(clientId)
            })
          } catch (error) {
            console.error(error)
          }
        })
      )
    },

    async getGoogleAnalytics() {
      return window.ga ?? this.loadGoogleAnalytics()
    },

    async loadGoogleAnalytics() {
      this.log.info('Loading Google Analytics script...')

      return importScript('https://www.google-analytics.com/analytics.js')
        .then(() => {
          let options = 'auto'

          if (document.location.protocol === 'file:') {
            options = {
              storage: 'none',
              checkProtocolTask: null,
            }
          }

          window.ga =
            window.ga ||
            function () {
              ;(window.ga.q = window.ga.q || []).push(arguments)
            }

          window.ga.l = 1 * new Date()
          window.ga('create', Configs.Google.Analytics.id, options)

          this.log.success('Loading Google Analytics script loaded!')

          return window.ga
        })
        .catch(() => this.log.error('Google Analytics script not loaded!'))
    },

    async trackPageview(page) {
      return this.trackEvent('pageview', {
        page: page || this.$route.fullPath,
      })
    },

    async trackEvent(eventName, data = {}) {
      const [clientId, utm] = await Promise.all([
        this.getGoogleAnalyticsClientId(),
        this.getUTM(),
      ])
      const userId = app.user?.id

      let event = {
        event: eventName,
        clientId,
      }

      if (utm) {
        event.utm_source = utm.source
        event.utm_medium = utm.medium
        event.utm_campaign = utm.campaign
      }

      if (userId) event.userId = userId

      event = {
        ...event,
        ...data,
      }

      return this.$gtm?.trackEvent?.(event)
    },

    /*async loadGoogleAnalytics() {
      return new Promise((resolve, reject) => {
        try {
          this.log.info('Loading Google Analytics...')

          let timeoutTimer

          const self = this
          const clientId = this.googleAnalyticsClientId
          const resolvePromise = () => {
            clearTimeout(timeoutTimer)
            resolve()
          }

          if (clientId)
            this.log.info(`Google Analytics client id found: ${clientId}`)
          else this.log.info('Generating Google Analytics client id...')

          let options = {
            id: Configs.Google.Analytics.id,
            //router: this.$router,
            disabled: true,
            set: [
              {
                field: 'checkProtocolTask',
                value: null,
              },
            ],
            fields: {
              clientId,
              userId: app.user?.id,
              storage: 'none',
            },
            checkDuplicatedScript: true,
            beforeFirstHit() {
              if (!clientId) {
                window.ga(tracker => {
                  const newClientId = tracker.get('clientId')

                  self.googleAnalyticsClientId = newClientId
                  self.log.success(
                    `Google Analytics client id created: ${newClientId}`,
                  )
                })
              }

              self.log.success('Google Analytics loaded!')
              resolvePromise()
            },
            autoTracking: {
              exception: true,
              pageviewTemplate(route) {
                return {
                  title: route.name,
                  page: route.fullPath,
                  location: window.location.href,
                }
              },
            },
          }

          if (app.environment === 'development')
            options.debug = {
              enabled: true,
              sendHitTask: false,
            }

          $app.use(VueAnalytics, options)

          timeoutTimer = setTimeout(() => {
            this.log.error('Timeout when loading Vue Analytics!')
            reject()
          }, 6 * 1000)
        } catch (e) {
          reject()
        }
      })
    },*/

    /*async loadSmartlook() {
      window.smartlook ||
        (function(d) {
          var o = (window.smartlook = function() {
              o.api.push(arguments)
            }),
            h = d.getElementsByTagName('head')[0]
          var c = d.createElement('script')
          o.api = new Array()
          c.async = true
          c.type = 'text/javascript'
          c.charset = 'utf-8'
          c.src = 'https://rec.smartlook.com/recorder.js'
          h.appendChild(c)
        })(document)

      smartlook('init', Configs.Smartlook.id)

      if (app.user) this.updateSmartlookUser(app.user)
    },

    async updateSmartlookUser(user) {
      if (window.smartlook && user) {
        smartlook('identify', user.id, {
          ...user,
          name: user.fullName,
        })
      }
    },*/

    disableBackButton(value = true) {
      switch (this.platform) {
        case 'App':
          const fn = event => {
            event.preventDefault()
          }

          if (value === true) {
            document.addEventListener('backbutton', fn)
          } else {
            document.removeEventListener('backbutton', fn)
          }
          break
      }

      return this
    },

    async loadOneSignal({ platform } = app) {
      return new Promise(async resolve => {
        try {
          const { VITE_ONESIGNAL_APP_ID: oneSignalAppId } = import.meta.env

          let oneSignal

          this.log.info('Loading OneSignal...')

          switch (platform) {
            case 'App':
              oneSignal = window.plugins?.OneSignal

              if (oneSignal?.setAppId) {
                oneSignal.setAppId(oneSignalAppId)
                oneSignal.promptForPushNotificationsWithUserResponse()
              } else if (oneSignal?.startInit) {
                oneSignal
                  .startInit(oneSignalAppId)
                  .inFocusDisplaying(
                    oneSignal.OSInFocusDisplayOption.Notification,
                  )
                  .iOSSettings({
                    kOSSettingsKeyAutoPrompt: true,
                  })
                  .endInit()
              }

              resolve(oneSignal)
              break

            case 'Web App':
              oneSignal = window.OneSignal

              if (!oneSignal) {
                oneSignal = window.OneSignal = []

                oneSignal.push(() => {
                  const oneSignalInstance = window.OneSignal

                  oneSignalInstance.on(
                    'notificationPermissionChange',
                    ({ to }) => {
                      if (to === 'granted') app.updateUserOnesignalDeviceId()
                    },
                  )

                  oneSignalInstance.on('subscriptionChange', isSubscribed => {
                    if (isSubscribed === true) app.updateUserOnesignalDeviceId()
                  })

                  oneSignalInstance.init({
                    appId: oneSignalAppId,
                    autoRegister: true,
                    notifyButton: {
                      enable: false /* Set to false to hide */,
                    },
                    welcomeNotification: {
                      disable: true,
                    },
                    promptOptions: {
                      /* actionMessage limited to 90 characters */
                      autoAcceptTitle: 'Clique em permitir',
                      actionMessage:
                        'Permitir receber notificações sobre a situação dos pedidos no Delivery In Box?',
                      /* acceptButtonText limited to 15 characters */
                      acceptButtonText: 'Permitir',
                      /* cancelButtonText limited to 15 characters */
                      cancelButtonText: 'Não permitir',
                      exampleNotificationCaption: '',
                      showCredit: false,
                    },
                  })

                  this.log.success('OneSignal loaded!')

                  resolve(oneSignalInstance)
                })

                await importScript(
                  'https://cdn.onesignal.com/sdks/OneSignalSDK.js',
                )
              }
              break
          }
        } catch (e) {
          console.error(e)
          resolve(false)
        }
      })
    },

    getReadingTimeInSeconds(string) {
      const stripTags = html => {
        const doc = new DOMParser().parseFromString(html, 'text/html')

        return doc.body.textContent || ''
      }

      string = stripTags(string)

      const wordsPerMinute = 200
      const numberOfWords = string.split(/\s/g).length
      const readTimeInMinutes = numberOfWords / wordsPerMinute
      const readTimeInSeconds = Math.ceil(readTimeInMinutes * 60)

      return readTimeInSeconds
    },

    async closeSnack() {
      return new Promise((resolve, reject) => {
        if (this.snackbarTimer) clearTimeout(this.snackbarTimer)

        this.$refs.snackbar?.close?.()

        setTimeout(resolve, 250)
      })
    },

    removeSnackbar({ id }) {
      this.snackbars = this.snackbars.filter(snackbar => snackbar.id !== id)

      return this
    },

    async showSnack({
      title,
      content,
      direction = 'left',
      duration,
      minDuration = 5000,
      notify = true,
    }) {
      if (!duration)
        duration =
          app.getReadingTimeInSeconds((title ?? '') + (content ?? '')) * 1000
      if (duration !== Infinity && duration < minDuration)
        duration = minDuration

      const snackbarId = uuid()
      const snackbar = {
        title,
        content,
        direction,
        duration,
        minDuration,
        notify,
        id: snackbarId,
        onClosed: () => {
          this.snackbars = this.snackbars.filter(
            snack => snack.id !== snackbarId,
          )
        },
      }

      this.snackbars = [snackbar, ...this.snackbars]

      await this.$nextTick()

      return this.$refs.snackbars.showSnackbar(snackbar)
    },

    changeStatusBarColor(color) {
      if (app.platform === 'App')
        document.addEventListener('deviceready', () => {
          window.StatusBar.backgroundColorByHexString(color)
        })

      return this
    },

    async getIntent() {
      return new Promise((resolve, reject) => {
        if (this.platform === 'App' && this.os === 'Android') {
          const intentShim = window.plugins?.intentShim

          let intent

          if (!intentShim) resolve()
          else intentShim.getIntent(resolve, reject)
        } else resolve()
      })
    },

    async getIntentRoute(intent) {
      return new Promise(async (resolve, reject) => {
        let route

        if (!intent) intent = await this.getIntent()

        if (intent) {
          const intentRoute = intent?.data?.split?.('#')?.[1]

          if (intentRoute) route = intentRoute
        }

        resolve(route)
      })
    },

    async getFirstRoute() {
      return new Promise(async resolve => {
        const intentRoute = await this.getIntentRoute()

        if (intentRoute) return resolve(intentRoute)

        const lastVisitedCityId = this.lastVisitedCityId

        let route

        if (
          lastVisitedCityId &&
          ['home', 'selectLocation'].includes(this.$route.name) === true
        )
          route = {
            name: 'selectCompany',
            params: {
              cityId: lastVisitedCityId,
            },
          }

        switch (app.platform) {
          case 'Web App':
            return resolve(route)
            break

          case 'App':
            const oneSignal = window.plugins?.OneSignal

            if (!oneSignal) return resolve(route)

            let isNotificationOpened = false

            oneSignal?.setNotificationOpenedHandler?.(data => {
              const { notification } = data
              const notificationData =
                notification?.payload?.additionalData ??
                notification?.additionalData

              this.trackEvent('pushNotificationOpened', notification)
              this.checkUTM({ notification })

              isNotificationOpened = true

              if (notificationData?.eval) eval(notificationData.eval)

              if (notificationData?.appLaunch)
                route = notificationData.appLaunch

              resolve(route)
            })

            oneSignal?.endInit?.()

            setTimeout(() => {
              if (isNotificationOpened === false) return resolve(route)
            }, 2000)
            break
        }
      })
    },

    async checkUTM({ notification } = {}) {
      let source
      let medium
      let campaign

      if (notification) {
        const notificationData =
          notification?.payload?.additionalData ?? notification?.additionalData

        source =
          notificationData?.utm_source ??
          notificationData?.utmSource ??
          notificationData?.utm?.source ??
          'onesignal'
        medium =
          notificationData?.utm_medium ??
          notificationData?.utmMedium ??
          notificationData?.utm?.medium ??
          'push'
        campaign =
          notificationData?.utm_campaign ??
          notificationData?.utmCampaign ??
          notificationData?.utm?.campaign ??
          notification?.notificationId
      } else {
        const routeQuery = this.$route.query

        source = routeQuery.utm_source
        medium = routeQuery.utm_medium
        campaign = routeQuery.utm_campaign
      }

      if (source) {
        const utm = {
          source,
          medium,
          campaign,
        }

        sessionStorage.setItem('utm', JSON.stringify(utm))

        return utm
      }
    },

    async getUTM() {
      let utm = sessionStorage.getItem('utm')

      utm = utm ? JSON.parse(utm) : undefined

      return utm
    },

    async goToFirstRoute() {
      const currentRoute = this.$route

      let targetRoute = await this.getFirstRoute()

      window.universalLinks?.subscribe?.(
        null,
        function universalLinksSubscribe({ hash, url }) {
          if (hash) targetRoute = hash
          else if (url) return window.location.replace(url)
        },
      )

      if (targetRoute && currentRoute.name !== targetRoute?.name)
        return this.goToScreen(targetRoute, true)

      return targetRoute
    },

    updateSupportUser({ fullName, email, phoneNumber } = this.user) {
      const support = this.support

      if (support.setUserName) {
        support
          .setUserName(fullName)
          .setUserEmail(email)
          .setUserPhone(phoneNumber)
      }
    },

    updateGoogleAnalyticsUser({ id } = this.user) {
      const $ga = this.$ga

      if (!$ga) return

      $ga.set('userId', id)
    },

    async updateUserOnesignalDeviceId(deviceId) {
      if (this.hasLoggedUser() === false) return
      if (!deviceId) deviceId = await this.getOnesignalDeviceId()

      const retryInterval = 5
      const retryFn = () =>
        window.setTimeout(
          () => app.updateUserOnesignalDeviceId(deviceId),
          retryInterval * 1000,
        )

      if (!deviceId) return retryFn()

      return app
        .callApi({
          method: 'PUT',
          action: 'me',
          ignoreRequestAuth: true,
          data: {
            d: deviceId,
          },
        })
        .catch(retryFn)
    },

    async getOnesignalDeviceId() {
      return new Promise(async resolve => {
        const oneSignal = await this.getOneSignal()

        if (oneSignal.getIds)
          oneSignal.getIds(({ userId }) => {
            resolve(userId)
          })
        else if (oneSignal.getDeviceState)
          oneSignal.getDeviceState(({ userId }) => resolve(userId))
        else if (oneSignal.getUserId) oneSignal.getUserId(resolve)
        else resolve()
      })
    },

    async scrollToTop() {
      window.scrollTo(0, 0)

      return this
    },

    async startUpdate(remotePackage) {
      return new Promise((resolve, reject) => {
        app.goToScreen(
          {
            name: 'update',
            params: {
              remotePackage,
              onInstalled: resolve,
              onFailed: reject,
            },
          },
          true,
        )
      })
    },

    async login({ email, password, facebookAccessToken }) {
      const { sessionToken } = await app.callApi({
        method: 'POST',
        version: 1,
        action: 'auth',
        data: {
          e: email,
          p: password,
          f: facebookAccessToken,
        },
      })

      return await app.saveLogin({
        sessionToken,
      })
    },

    validatePhoneNumber({ phone, country = 'BR' }) {
      if (typeof phone !== 'string') return

      const parsedPhone = parsePhoneNumberFromString(phone, country)

      return {
        isValid: parsedPhone ? parsedPhone.isValid() : false,
        isPossible: parsedPhone ? parsedPhone.isPossible() : false,
      }
    },

    convertPhoneNumber(phoneNumber) {
      if (!phoneNumber) phoneNumber = ''

      const phoneNumberMask = '(00) 90000-0000'
      const formatter = new StringMask(phoneNumberMask)
      const countryFormatter = new StringMask('+00 '.concat(phoneNumberMask))

      let onlyNumbers = getOnlyNumbers(phoneNumber)

      return {
        numbers: onlyNumbers,
        formatted: formatter.apply(onlyNumbers.substr(2)),
        formattedWithCountry: countryFormatter.apply(onlyNumbers),
      }
    },

    getInfo() {
      const platform = this.platform
      const version = this.version

      return {
        platform,
        version,
        full: `${platform} v${version}`,
      }
    },

    hasLoggedUser() {
      return [null, undefined].includes(app.user) === false
    },

    isWifiConnection() {
      if (navigator.connection) return navigator.connection.type === 'wifi'
    },

    async dialog({
      buttons = [],
      showImmediately = true,
      returnOnButtonAction = false,
      ...options
    }) {
      return new Promise(async resolve => {
        const { dialogs } = this

        let newDialog
        let dialogComponent

        buttons.forEach(button => {
          const onClickFn = button.onClick

          if (!onClickFn) button.onClick = () => newDialog.close()
          else button.onClick = () => onClickFn(newDialog)
        })

        if (returnOnButtonAction === true)
          buttons.forEach(button => {
            const buttonClickFunction = button.onClick

            button.onClick = async modal => {
              let buttonClickResult = await buttonClickFunction(modal)

              if (button.returnValue !== undefined)
                buttonClickResult = button.returnValue

              resolve(buttonClickResult)
            }
          })

        let dialogIndex = dialogs.length

        newDialog = {
          ...options,
          id: dialogIndex,
          buttons,
          onClosed: async () => {
            if (typeof options.onClosed === 'function') await options.onClosed()

            dialogs.splice(dialogIndex, 1)
          },
          close() {
            return dialogComponent.close()
          },
        }

        dialogIndex = dialogs.push(newDialog) - 1

        await this.$nextTick()

        dialogComponent = this.$refs[`dialog-${newDialog.id}`]?.[0]

        if (showImmediately === true) await dialogComponent.open()

        if (returnOnButtonAction === false) resolve(newDialog)
      })
    },

    alert(customOptions = {}) {
      const dialogOptions = {
        buttons: [
          {
            content: 'Ok',
            primary: true,
            onClick: modal => modal.close(),
          },
        ],
        ...customOptions,
      }
      const buttonsWithoutClick = dialogOptions.buttons.filter(
        button => !button.onClick,
      )

      buttonsWithoutClick.forEach(
        button => (button.onClick = modal => modal.close()),
      )

      if (dialogOptions.onClose) {
        dialogOptions.onClosed = dialogOptions.onClose
        delete dialogOptions.onClose
      }

      return app.dialog(dialogOptions)
    },

    async confirm(customOptions = {}) {
      const customOptionsButtons = customOptions.buttons
      const onCancel = modal => modal.close()
      const onOk = async modal => {
        modal.close()

        if (typeof customOptions.onOk === 'function')
          await customOptions.onOk(modal)
      }

      if (typeof customOptionsButtons === 'object') {
        const newButtons = []

        if (customOptionsButtons.ok) {
          newButtons.push({
            content: customOptionsButtons.ok.text ?? 'Ok',
            onClick: onOk,
          })
        }

        if (customOptionsButtons.cancel) {
          newButtons.push({
            content: customOptionsButtons.cancel.text ?? 'Cancelar',
            onClick: onCancel,
          })
        }

        customOptions.buttons = newButtons
      }

      const dialogOptions = {
        buttons: [
          {
            content: 'Ok',
            primary: true,
            onClick: onOk,
          },

          {
            content: 'Cancelar',
            onClick: onCancel,
          },
        ],
        ...customOptions,
      }

      if (dialogOptions.onClose) {
        dialogOptions.onClosed = dialogOptions.onClose
        delete dialogOptions.onClose
      }

      return app.dialog(dialogOptions)
    },

    showLoading(customOptions = {}) {
      console.log('showLoading OBSOLETO')
    },

    async callApi(options = {}) {
      const getApiClient = () => {
        const cacheKey = '__callApiClient'

        let client = window[cacheKey]

        if (client === undefined) {
          const { VITE_API_ORIGIN: API_ORIGIN } = import.meta.env

          client = axios.create({ baseURL: `${API_ORIGIN}/` })

          axiosRetry(client, {
            retries: Infinity,
            shouldResetTimeout: true,
            onRetry: (retryCount, error, requestConfig) =>
              console.info('Retrying request...', {
                retryCount,
                error,
                requestConfig,
              }),
            retryDelay: () => 3000,
            retryCondition: err =>
              err?.message?.includes?.('timeout') === true ||
              axiosRetry.isNetworkOrIdempotentRequestError(err),
          })

          window[cacheKey] = client
        }

        return client
      }
      const client = getApiClient()
      const apiVersion = options.version ?? app.api.defaultVersion

      let requestConfig = {
        ...options,
        method: options.method ?? 'GET',
        url: `v${apiVersion}/${options.action ?? options.url}`,
        params: options.query ?? options.queries ?? options.params,
        data: options.data ?? options.body,
        timeout: options.timeout ?? 20000,
        responseType: options.responseType ?? 'json',
        headers: options.headers ?? {},
        'axios-retry': options['axios-retry'],
      }

      const sessionToken =
        options?.sessionToken ??
        options?.apiToken ??
        options?.token ??
        (await app.getSessionToken())

      if (sessionToken)
        requestConfig.headers.Authorization = `Bearer ${sessionToken}`

      requestConfig.headers['Client-Name'] = app.platform
      requestConfig.headers['Client-Version'] = app.version

      return client
        .request(requestConfig)
        .then(({ data }) => data)
        .catch(error => {
          console.error(error)

          const body = error?.response?.data
          const ignoreRequestAuth = options.ignoreRequestAuth === true

          if (ignoreRequestAuth === false) {
            const firstError = body?.errors?.[0] ?? body
            const requestLogin =
              firstError?.requestAuth === true ||
              [
                'API_TOKEN_NOT_SENT',
                'API_TOKEN_NOT_FOUND',
                'API_TOKEN_EXPIRED',
                'API_USER_NOT_FOUND',
              ].includes(firstError?.code)

            if (requestLogin === true) {
              this.logout()
              this.goToLogin()

              return false
            }
          }

          return Promise.reject(body)
        })
    },

    async logoutFromApi(sessionToken) {
      try {
        if (sessionToken === undefined)
          sessionToken = await app.getSessionToken()

        return await app.callApi({
          method: 'POST',
          version: 1,
          action: 'me/logout',
          sessionToken,
        })
      } catch (e) {
        console.error(e)
        return
      }
    },

    async logout(sessionToken) {
      if (!sessionToken) sessionToken = await app.getSessionToken()

      app.user = undefined

      return Promise.all([
        app.removeSessionToken(),
        app.logoutFromApi(sessionToken),
      ])
    },

    goToLogin(redirect, replace = false) {
      if (!redirect) redirect = app.getCurrentRoute()
      if (redirect) redirect = JSON.stringify(redirect)

      app.goToScreen(
        {
          name: 'login',
          query: {
            redirect,
          },
        },
        replace,
      )
    },

    formatToLocaleDate(value) {
      const date = new Date(value)
      const formatted = date.toLocaleString().replace(' ', ' às ')

      return {
        iso: value,
        date: date.toLocaleDateString(),
        formatted,
        complete:
          formatted +
          ' <i class="text-muted">(<time class="timeago" datetime="' +
          value +
          '"></time>)</i>',
      }
    },

    formatDate(value, format = '%d/%m/%Y às %H:%i:%s') {
      const date = new Date(value),
        day = date.getDate().toString().padStart(2, '0'),
        month = (date.getMonth() + 1).toString().padStart(2, '0'),
        year = date.getFullYear(),
        hours = date.getHours().toString().padStart(2, '0'),
        minutes = date.getMinutes().toString().padStart(2, '0'),
        seconds = date.getSeconds().toString().padStart(2, '0')

      return format
        .replace('%d', day)
        .replace('%m', month)
        .replace('%Y', year)
        .replace('%H', hours)
        .replace('%i', minutes)
        .replace('%s', seconds)
    },

    goBackScreen() {
      this.$router.go(-1)

      return this
    },

    getCurrentRoute() {
      const currentRoute = this.$route

      return clone({
        name: currentRoute.name,
        params: currentRoute.params,
        query: currentRoute.query,
        meta: currentRoute.meta,
      })
    },

    goToScreen(targetRoute, replaceCurrentRoute = false) {
      if (!targetRoute) return

      const routerMethodName = replaceCurrentRoute ? 'replace' : 'push'

      app.isGoToScreen = true

      return this.$router[routerMethodName](targetRoute)
        .catch(console.error)
        .finally(e => (app.isGoToScreen = false))
    },
  },
}
</script>

<style lang="scss">
@use 'sass:color';
@use 'sass:list';
@use 'sass:math';

// @import '~sanitize.css';
@use '@/assets/fonts/axiforma/style.scss';
@use '@/styles/settings.scss' as *;
@use '@/styles/functions.scss' as *;

:root {
  --primary-color: #{$primaryColor};
  --primary-color-rgb: #{color.channel($primaryColor, 'red', $space: rgb)},
    #{color.channel($primaryColor, 'green', $space: rgb)},
    #{color.channel($primaryColor, 'blue', $space: rgb)};

  --secondary-color: #{$secondaryColor};
  --secondary-color-rgb: #{color.channel($secondaryColor, 'red', $space: rgb)},
    #{color.channel($secondaryColor, 'green', $space: rgb)},
    #{color.channel($secondaryColor, 'blue', $space: rgb)};

  --tertiary-color: #{$tertiaryColor};
  --tertiary-color-rgb-rgb: #{color.channel($tertiaryColor, 'red', $space: rgb)},
    #{color.channel($tertiaryColor, 'green', $space: rgb)},
    #{color.channel($tertiaryColor, 'blue', $space: rgb)};
}

.text-color-secondary {
  color: var(--secondary-color);
}

// html,
// body {
//   max-width: 100vw;
//   min-width: 100vw;
//   overflow-x: hidden;
// }

html {
  box-sizing: border-box;
  text-size-adjust: 100%;

  *,
  ::before,
  ::after {
    box-sizing: border-box;
  }
}

// body,
// .app-layout {
//   -webkit-overflow-scrolling: auto;
//   touch-action: pan-y;
//   // pointer-events: auto;
//   // overscroll-behavior-y: contain;
// }

body {
  // overscroll-behavior: none;
  -webkit-user-drag: none;
  -ms-content-zooming: none;
  text-size-adjust: none;
  margin: 0;
  padding-bottom: env(safe-area-inset-bottom);
  font-family: 'Axiforma';
  text-rendering: optimizeLegibility;
  min-width: 100%;
  color: #000;
  background-color: #fff;
  line-height: 1.5;
}

.scroll-smooth {
  scroll-behavior: smooth;
}

.sm-line-height {
  line-height: 1.25;
}

.xs-line-height {
  line-height: 1;
}

img {
  max-width: 100%;
  display: block;
}

input,
button,
textarea,
select {
  font: inherit;
}

svg {
  fill: transparent;
}

fieldset {
  margin: 0;
  padding: 0;
  border: 0;
}

#app {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

#loadingContainer {
  display: flex;
  min-height: var(--viewport-height);
  align-items: flex-start;

  > img {
    display: block;
    max-width: 50vw;
    max-height: 80px;
  }
}

p {
  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.no-top-margin {
  margin-top: 0;
}

.container {
  padding: 1.5rem;

  > .md-subheader {
    padding-left: 0;
    padding-right: 0;
  }
}

.side-container {
  padding-left: 1rem;
  padding-right: 1rem;
}

.no-padding-top {
  padding-top: 0;
}

.no-padding-bottom {
  padding-bottom: 0;
}

.md-card.no-shadow {
  box-shadow: none;
  padding-bottom: 0;
}

.dialog-loading-text {
  align-self: center;
  line-height: 120%;
}

.float-left {
  float: left;
}

.float-right {
  float: right;
}

.text-center {
  text-align: center;
}

.md-actions {
  :first-child {
    margin-left: 0;
  }

  :last-child {
    margin-right: 0;
  }
}

.md-button {
  &.md-button-big {
    font-size: 1rem;
    padding-top: 4px;
    padding-bottom: 4px;
  }

  &.md-button-fluid {
    display: block;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  &.md-fab {
    border-radius: 50% !important;
  }
}

.md-toolbar-fixed {
  $paddingTop: 64px;

  &:not(.md-sidenav) {
    padding-top: $paddingTop;
  }

  .md-toolbar-fixed {
    padding-top: 0;
  }

  &.md-sidenav {
    .md-sidenav-content {
      padding-top: $paddingTop;
    }
  }

  > header,
  .md-toolbar {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 3;
  }
}

.md-list-text-container {
  &.no-ellipsis {
    > * {
      white-space: normal;
    }
  }
}

.md-list {
  padding: 0;

  .md-list-item-expand {
    &:before,
    &:after {
      content: none;
    }
  }

  &.md-triple-line {
    .md-list-item {
      padding-top: 16px;
      padding-bottom: 16px;

      .md-list-item-container {
        min-height: 0 !important;
      }
    }
  }

  &.with-border {
    .md-subheader + .md-list-item {
      border-top: 0;
    }

    .md-list-item {
      border-top: 1px solid rgba(0, 0, 0, 0.12);

      &:first-child {
        border-top: 0;
      }
    }
  }
}

.md-list-text-container-description {
  color: rgba(0, 0, 0, 0.54) !important;
}

.clearfix {
  zoom: 1;

  &::before,
  &::after {
    content: '';
    display: table;
  }

  &::after {
    clear: both;
  }
}

.no-side-margin {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.no-side-padding {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.no-padding {
  padding: 0 !important;
}

.md-list {
  &.md-double-line {
    .md-list-item-expand {
      min-height: 72px;
    }
  }

  .md-list-item-container {
    $verticalPadding: 12px;
    padding-top: $verticalPadding;
    padding-bottom: $verticalPadding;

    &.md-button {
      line-height: 100%;
    }
  }
}

.extra-padding {
  .md-list-item-container {
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
  }
}

.md-avatar {
  > .md-image {
    border-radius: 40px;
  }
}

.md-header {
  .md-title {
    line-height: 100%;
  }

  .md-subtitle {
    display: block;
  }
}

.badge {
  border-radius: 3px;
  white-space: nowrap;
  display: inline-block;
  padding: 8px 12px;
  line-height: 100%;
  text-align: center;

  &.rounded {
    border-radius: 50%;
    width: 32px;
    height: 32px;
  }

  &.badge-small {
    $padding: 0.3em;

    font-size: 11px;
    padding: $padding ($padding + 0.1em);
  }

  &.badge-red {
    background-color: #b71c1c;
    color: #fff;
  }

  &.badge-grey {
    background-color: #757575;
    color: #fff;
  }

  &.badge-yellow {
    background-color: #f4d142;
    color: #fff;
  }
}

.md-checkbox {
  .md-checkbox-container {
    min-width: 20px;
    background-color: #fff;
  }

  .md-checkbox-label {
    height: auto !important;
  }
}

.md-input-container {
  &:last-child {
    margin-bottom: 0;
  }
}

.md-radio-label {
  height: auto !important;
}

.md-radio {
  .md-radio-container {
    background-color: #fff;
    min-width: 20px;
    min-height: 20px;

    &::after {
      top: 2px;
      left: 2px;
    }
  }
}

.md-button {
  &.md-fab {
    overflow: visible !important;

    .badge {
      position: absolute;
      top: 0;
      right: 0;
      transform: translate(0, -25%);
    }
  }
}

.support-padding {
  padding-bottom: 85px;
}

.md-fab-fixed {
  position: fixed !important;
}

.md-toolbar {
  flex-wrap: nowrap;

  &.spaced {
    $sidePadding: 1.2rem;
    padding-left: $sidePadding;
    padding-right: $sidePadding;
  }

  > .md-header {
    display: flex;

    > .md-avatar {
      margin-right: 7px;
      margin-left: 0;
    }
  }

  .md-title {
    line-height: 100%;

    &:first-child {
      margin-left: 0 !important;
    }
  }
}

.md-input-container {
  &.no-label {
    padding-top: 0;
    min-height: 0;
  }
}

.section {
  &.container {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.md-button-toggle {
  > .md-button {
    $border-radius: 3px;

    border: 1px solid #bbb;
    border-left-width: 0;

    &:first-child {
      border-left-width: 1px;
      border-radius: $border-radius 0 0 $border-radius;
    }

    &:last-child {
      border-radius: 0 $border-radius $border-radius 0;
    }

    &.md-toggle {
      border-color: var(--primary-color);
    }
  }
}

.md-radios-columns-2 {
  > .md-radio {
    min-width: 47%;
  }
}

.md-radio {
  .description {
    display: block;
  }
}

.md-tabs {
  .md-tab {
    &.no-padding {
      padding: 0;
    }
  }
}

.lazy-image {
  width: 100%;
  height: 100%;
}

.md-dialog-container {
  outline: 0;
}

.md-subheader {
  &.md-raised {
    background-color: #eee;
  }
}

.zopim {
  display: none !important;
}

.text-muted {
  opacity: 0.54;
}

.fieldset-clean {
  margin: 0;
  padding: 0;
  border: 0;
}

.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.disabled-with-click {
  opacity: 0.5;
}

.md-speed-dial {
  &.compact {
    .md-button {
      display: none;

      &:first-child {
        display: block;
      }
    }

    &.md-active {
      .md-button {
        display: block;
      }
    }
  }
}

.md-button.md-icon-button .md-icon {
  position: static;
}

.md-card-actions {
  padding: 1em;
}

.text-ellipsis {
  max-width: 42vw;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.md-radio.md-primary.md-checked .md-radio-container,
.md-theme-default.md-checkbox.md-primary.md-checked .md-checkbox-container {
  border-color: var(--primary-color) !important;
}

.text-primary,
.md-icon.md-primary,
.md-subheader.md-primary,
.md-input-container.md-input-focused input,
.md-input-container.md-input-focused label,
.md-input-container.md-input-focused.md-input-required label::after,
.md-input-container.md-input-focused .md-icon:not(.md-icon-delete),
.md-list .md-list-item.md-primary .md-list-item-container {
  color: var(--primary-color) !important;
}

.md-spinner {
  .md-spinner-path {
    stroke: var(--primary-color) !important;
  }
}

.bg-primary,
.md-toolbar,
.md-tabs-navigation,
.md-radio.md-primary .md-radio-container::after,
.md-input-container.md-input-focused::after,
.md-switch.md-checked.md-primary .md-switch-thumb,
.md-button-toggle.md-primary .md-toggle,
.md-tab-header-navigation-button,
.md-button:not([disabled]).md-primary.md-raised:not(.md-theme-teal),
.md-theme-default.md-checkbox.md-primary.md-checked .md-checkbox-container {
  background-color: var(--primary-color) !important;
}

.md-switch.md-checked.md-primary .md-switch-container {
  background-color: rgba(var(--primary-color-rgb), 0.5) !important;
}

.md-theme-default.md-boards > .md-boards-navigation .md-board-header.md-active,
.md-theme-default.md-boards > .md-boards-navigation .md-board-header:active,
.md-theme-default.md-button:not([disabled]).md-primary:not(.md-icon-button):not(
    .md-raised
  ) {
  color: var(--primary-color) !important;
}

.md-ink-ripple {
  display: none !important;
}

.md-theme-default.md-list {
  .md-list-item {
    &.md-primary {
      .md-list-item-container {
        > .md-icon {
          color: inherit;
        }
      }
    }
  }
}

.lg-backdrop.in {
  opacity: 0.8 !important;
}

.hidden {
  display: none !important;
}

.padding,
.pd {
  padding: 1rem;
}

.pd-bt-0 {
  padding-bottom: 0;
}

.mg-bt-0 {
  margin-bottom: 0;
}

.subheader {
  text-transform: uppercase;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 1rem;
  background-color: #ebebeb;
  color: rgba(0, 0, 0, 0.5);
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  font-weight: 500;

  > *:not(.title) {
    font-weight: normal;
  }
}

.fixed-container {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;

  .button-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #009688;
    color: rgba(255, 255, 255, 0.87);
    padding: 0.5rem 16px;
    min-height: 52px;

    .label {
      text-transform: uppercase;
      margin-left: auto;
      margin-right: auto;
    }

    .badge {
      background-color: rgba(0, 0, 0, 0.3);
      color: rgba(255, 255, 255, 0.87);
    }
  }
}

.flat-button {
  border-radius: 0;
  margin: 0;
}

.md-button {
  &.high-padding {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
}

.fixed-container-padding {
  padding-bottom: 52px;
}

.md-snackbar {
  z-index: 98;

  > .md-snackbar-content {
    justify-content: start;
  }

  .image {
    margin-left: 0;
    margin-right: 1rem;
  }

  &.notify {
    padding: 0.5rem;

    .md-snackbar-container {
      border-radius: 5px;
    }
  }

  &.positive {
    $color: #43a047;

    .md-snackbar-container {
      background-color: $color;
    }
  }

  .icon + .content {
    margin-left: 0.5rem;
    padding-left: 0.5rem;
    border-left: 1px solid rgba(255, 255, 255, 0.3);
  }
}

.muted-text-color {
  opacity: 0.54;
}

@each $color in $colors {
  $colorName: list.nth($color, 1);
  $color: list.nth($color, 2);

  .#{$colorName}-text-color {
    color: $color !important;
  }

  .#{$colorName}-background-color {
    $textColor: get-text-color($color);

    background-color: $color !important;
    color: $textColor !important;

    &.inverted {
      background-color: $textColor !important;
      color: $color !important;
    }

    .#{$colorName}-background-color {
      $backgroundColor: color.adjust($color, $lightness: -8%);
      $textColor: get-text-color($backgroundColor);
      background-color: $backgroundColor !important;
      color: $textColor !important;

      &.inverted {
        background-color: $textColor !important;
        color: $backgroundColor !important;
      }
    }
  }
}

.app-dialog {
  .app-layout-header + .app-layout-content {
    > .app-card {
      padding-top: 0;
    }
  }

  .app-layout-content {
    > .app-card {
      display: block !important;
      padding-bottom: 0;
    }
  }
}

.text-uppercase {
  text-transform: uppercase;
}

.ml-auto {
  margin-left: auto !important;
}

.mr-auto {
  margin-right: auto;
}

.line-clamp-1 {
  -webkit-line-clamp: 1;
}

.line-clamp-2 {
  -webkit-line-clamp: 2;
}

.line-clamp-3 {
  -webkit-line-clamp: 3;
}

.negative-top-margin {
  margin-top: -#{$defaultPadding};
}

.d-block {
  display: block;
}

.text-right {
  text-align: right;
}

.pb-0 {
  padding-bottom: 0 !important;
}

.pb-1 {
  padding-bottom: 0.25rem;
}

.pb-2 {
  padding-bottom: 0.5rem;
}

.pb-3 {
  padding-bottom: 0.75rem;
}

.pb-4 {
  padding-bottom: 1rem;
}

.pb-5 {
  padding-bottom: 1.25rem;
}

.pb-6 {
  padding-bottom: 1.5rem;
}

.p-0 {
  padding: 0 !important;
}

.pt-0 {
  padding-top: 0 !important;
}

.mt-0 {
  margin-top: 0 !important;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 0.75rem;
}

.mt-4 {
  margin-top: 1rem;
}

.w-100 {
  width: 100%;
}

.m-0 {
  margin: 0;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-5 {
  margin-bottom: 1.25rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-7 {
  margin-bottom: 1.75rem;
}

.self-start {
  align-self: flex-start;
}

.self-end {
  align-self: flex-end;
}

.self-center {
  align-self: center;
}

.flex-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;

  &.extra-gap {
    gap: 2rem;
  }
}

.app-default-layout {
  padding-bottom: var(--app-footer-height, 0);

  > .app-layout-footer {
    position: fixed !important;
    bottom: 0;
    left: 0;
    right: 0;
  }
}

.rate-last-order {
  align-self: center;
  max-width: 100vw;
  padding: 1rem;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.5s;
  will-change: transform;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  $duration: 1s;
  transition:
    opacity #{$duration},
    transform #{$duration};
  will-change: opacity transform;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.p-1 {
  padding: 0.25rem;
}

.p-2 {
  padding: 0.5rem;
}

.p-3 {
  padding: 0.75rem;
}

.p-4 {
  padding: 1rem;
}

.p-5 {
  padding: 1.25rem;
}

.p-6 {
  padding: 1.5rem;
}

.p-7 {
  padding: 1.75rem;
}

.p-8 {
  padding: 2rem;
}

.py-2 {
  $padding: 0.5rem;
  padding-top: $padding;
  padding-bottom: $padding;
}

.px-1 {
  $padding: 0.25rem;
  padding-left: $padding;
  padding-right: $padding;
}

.px-2 {
  $padding: 0.5rem;
  padding-left: $padding;
  padding-right: $padding;
}

.px-3 {
  $padding: 0.75rem;
  padding-left: $padding;
  padding-right: $padding;
}

.px-4 {
  $padding: 1rem;
  padding-left: $padding;
  padding-right: $padding;
}

.px-5 {
  $padding: 1.25rem;
  padding-left: $padding;
  padding-right: $padding;
}

.px-6 {
  $padding: 1.5rem;
  padding-left: $padding;
  padding-right: $padding;
}

.mx-2 {
  $margin: 0.5rem;
  margin-left: $margin;
  margin-right: $margin;
}

.pl-1 {
  padding-left: 0.25rem;
}

.pl-2 {
  padding-left: 0.5rem;
}

.pr-0 {
  padding-right: 0 !important;
}

.pr-1 {
  padding-right: 0.25rem;
}

.pr-2 {
  padding-right: 0.5rem;
}

.pt-0 {
  padding-top: 0;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mt--4 {
  margin-top: -1rem;
}

.mt--6 {
  margin-top: -1.5rem;
}

.mb--6 {
  margin-bottom: -1.5rem;
}

.mx--6 {
  margin-left: -1.5rem;
  margin-right: -1.5rem;
}

.block {
  display: block;
}

.flex {
  display: flex;
}

.flex-1 {
  flex: 1 1 0%;
}

.flex-auto {
  flex: 1 1 auto;
}

.flex-col {
  flex-direction: column;
}

.items-center {
  align-items: center;
}

.items-start {
  align-items: flex-start;
}

.items-end {
  align-items: flex-end;
}

.justify-evenly {
  justify-content: space-evenly;
}

.justify-between {
  justify-content: space-between;
}

.justify-center {
  justify-content: center;
}

.justify-end {
  justify-content: flex-end;
}

.gap-1 {
  gap: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 0.75rem;
}

.gap-4 {
  gap: 1rem;
}

.gap-5 {
  gap: 1.25rem;
}

.gap-6 {
  gap: 1.5rem;
}

.gap-7 {
  gap: 1.75rem;
}

.gap-8 {
  gap: 2rem;
}

.gap-y-1 {
  row-gap: 0.25rem;
}

.gap-y-2 {
  row-gap: 0.5rem;
}

.gap-y-3 {
  row-gap: 0.75rem;
}

.gap-y-4 {
  row-gap: 1rem;
}

.mb--8 {
  margin-bottom: -2rem;
}

.leading-none {
  line-height: 1;
}

.leading-tight {
  line-height: 1.25;
}

.leading-snug {
  line-height: 1.375;
}

.leading-normal {
  line-height: 1.5;
}

.text-slate-50 {
  color: rgb(248 250 252);
}

.text-slate-100 {
  color: rgb(241 245 249);
}

.text-slate-200 {
  color: rgb(226 232 240);
}

.text-slate-300 {
  color: rgb(203 213 225);
}

.text-slate-400 {
  color: rgb(148 163 184);
}

.text-slate-500 {
  color: rgb(100 116 139);
}

.text-slate-600 {
  color: rgb(71 85 105);
}

.text-slate-700 {
  color: rgb(51 65 85);
}

.text-zinc-50 {
  color: rgb(250 250 250);
}

.text-zinc-100 {
  color: rgb(244 244 245);
}

.text-zinc-200 {
  color: rgb(228 228 231);
}

.text-zinc-300 {
  color: rgb(212 212 216);
}

.text-zinc-400 {
  color: rgb(161 161 170);
}

.text-zinc-500 {
  color: rgb(113 113 122);
}

.text-zinc-600 {
  color: rgb(82 82 91);
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.text-base {
  font-size: 1rem;
  line-height: 1.5rem;
}

.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}

.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}

.color-primary {
  color: var(--primary-color);
}

.text-left {
  text-align: left;
}

.text-center {
  text-align: center;
}

.no-underline {
  text-decoration: none;
}

.leading-4 {
  line-height: 1rem;
}

.leading-5 {
  line-height: 1.25rem;
}

.leading-6 {
  line-height: 1.5rem;
}

.leading-none {
  line-height: 1;
}

.leading-tight {
  line-height: 1.25;
}

.overflow-hidden {
  overflow: hidden;
}

.flex-wrap {
  flex-wrap: wrap;
}

.text-shadow-3 {
  $strokeWidth: 6px;
  $shadowColor: #000;
  color: #fff;
  text-shadow:
    -#{$strokeWidth} -#{$strokeWidth} 0 $shadowColor,
    0 -#{$strokeWidth} 0 $shadowColor,
    #{$strokeWidth} -#{$strokeWidth} 0 $shadowColor,
    #{$strokeWidth} 0 0 $shadowColor,
    #{$strokeWidth} #{$strokeWidth} 0 $shadowColor,
    0 #{$strokeWidth} 0 $shadowColor,
    -#{$strokeWidth} #{$strokeWidth} 0 $shadowColor,
    -#{$strokeWidth} 0 0 $shadowColor,
    0 #{$strokeWidth * 2} 0 $shadowColor,
    #{$strokeWidth * 0} #{$strokeWidth * 1} 0 $shadowColor,
    #{$strokeWidth * 0.25} #{$strokeWidth * 1.25} 0 $shadowColor,
    #{$strokeWidth * 0.5} #{$strokeWidth * 1.5} 0 $shadowColor,
    #{$strokeWidth * 0.75} #{$strokeWidth * 1.75} 0 $shadowColor,
    #{$strokeWidth * 1} #{$strokeWidth * 2} 0 $shadowColor,
    $strokeWidth #{$strokeWidth * 1} 0 $shadowColor,
    $strokeWidth #{$strokeWidth * 1.25} 0 $shadowColor,
    $strokeWidth #{$strokeWidth * 1.5} 0 $shadowColor,
    $strokeWidth #{$strokeWidth * 1.75} 0 $shadowColor,
    $strokeWidth #{$strokeWidth * 2} 0 $shadowColor,
    #{$strokeWidth * 2} 0 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 0} 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 0.25} 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 0.5} 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 0.75} 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 1} 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 1.25} 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 1.5} 0 $shadowColor,
    #{$strokeWidth * 2} #{$strokeWidth * 1.75} 0 $shadowColor;
}

.text-shadow-2 {
  $strokeWidth: 2px;
  $shadowColor: #000;
  color: #fff;
  text-shadow:
    -#{$strokeWidth} -#{$strokeWidth} 0 $shadowColor,
    0 -#{$strokeWidth} 0 $shadowColor,
    #{$strokeWidth} -#{$strokeWidth} 0 $shadowColor,
    #{$strokeWidth} 0 0 $shadowColor,
    #{$strokeWidth} #{$strokeWidth} 0 $shadowColor,
    0 #{$strokeWidth} 0 $shadowColor,
    -#{$strokeWidth} #{$strokeWidth} 0 $shadowColor,
    -#{$strokeWidth} 0 0 $shadowColor,
    0 #{$strokeWidth * 2} 0 $shadowColor,
    $strokeWidth #{$strokeWidth * 2} 0 $shadowColor;
}

.text-shadow-1 {
  $strokeWidth: 1px;
  $shadowColor: #000;
  color: #fff;
  text-shadow:
    -#{$strokeWidth} -#{$strokeWidth} 0 $shadowColor,
    0 -#{$strokeWidth} 0 $shadowColor,
    #{$strokeWidth} -#{$strokeWidth} 0 $shadowColor,
    #{$strokeWidth} 0 0 $shadowColor,
    #{$strokeWidth} #{$strokeWidth} 0 $shadowColor,
    0 #{$strokeWidth} 0 $shadowColor,
    -#{$strokeWidth} #{$strokeWidth} 0 $shadowColor,
    -#{$strokeWidth} 0 0 $shadowColor;
}
</style>
